import { Component, booleanAttribute, ChangeDetectorRef, AfterContentChecked } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { RestService } from 'src/app/services/rest/rest.service';
import { CountryNames, CifDetails, CorporateUserDetails, CorporateUser, GetCompanyAccounts, GenericResponse, UserProfile } from 'src/app/interfaces/apiresponses';
import { IItem } from '@coreui/angular-pro'
import { NumberFormatPipe } from 'src/app/shared/number.pipe';
import * as _ from 'lodash';
import { DatePipe } from '@angular/common';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
registerLocaleData(localeEn, 'en');

type CifCurrencyCode = 'USD' | 'EUR'

interface CifThresholdCell {
  threshold: number
  unlimited: boolean
  valid: any
}

@Component({
  selector: 'app-manage-customer',
  templateUrl: './manage-customer.component.html',
  styleUrls: ['./manage-customer.component.scss'],
  providers: [NumberFormatPipe, DatePipe]
})
export class ManageCustomerComponent implements AfterContentChecked {
  // Form settings
  isNew: boolean = false;
  submitted = false;
  bUndefined!: boolean
  bNewCustomer: boolean = false
  bCustomerProfileEdited: boolean = false
  bCustomerPermissionsEdited: boolean = false
  bLoading: boolean = false
  bShowCifSearchScreen: boolean = true
  bShowCustomerDetailsScreen: boolean = false
  bViewOnly: boolean = true
  bEditData: boolean = true
  authorityLevelTabLabel: string = 'CIF Level Authorities'
  approvedCustomerProfile: any
  bCustomerRoleOrAuthorityChanged: boolean = false
  bShowingApprovedData: boolean = true
  topScreenMessage: string = 'You are looking at the approved permissions of this customer. Click to see the permissions pending approval.'
  bHasCustomerMatrixApprovalPending: boolean = false
  bUserActive: boolean = false
  bUserRejected: boolean = false
  selectedSearchButton: number = 1
  searchBtnColor = 'secondary'
  showRejectCustomerBtnColor = 'secondary'
  showAllRejectCustomerBtnColor = 'secondary'
  createCustomerBtnColor = 'secondary'
  rejectionReason: string = ''
  isCustomerRejected: boolean = false

  // Alert buttons
  bAlert = false
  alertHeaderText = ""
  alertBodyText = ""
  bShowSecondAlertButton = false
  alertSecondButtonText = ""
  alertCloseButtonText = "Close"
  alertBoxData: any

  // Country list
  countryList: CountryNames | undefined

  // Dates
  minDateOfBirth: Date = new Date()
  minIDExpiryDate: Date = new Date()

  // Selected Customer Details
  selectedItem: any = {}
  originalCustomerDetails: any = {}
  companyId: number = 0
  bSelectedCustomerCIFLevelAuthority: boolean = false
  selectedCustomerDateOfBirth?: Date | null = new Date()
  selectedCustomerIDExpiresOn?: Date | null = new Date()
  selectedCustomerIDType: string = 'Passport'
  selectedCustomerGender: string = 'Male'
  selectedCustomerNationality: string = ''
  bIsSelectedCustomerAuthorityDefined: boolean = false
  selectedCustomerRole: string = 'Maker'
  bIsSelectedCustomerMaker: boolean = false
  selectedCustomerAuthorityLevel: string = 'CIF Level'
  bSelectedCustomerCreateSB: boolean = false
  bSelectedCustomerCreateCurrent: boolean = false
  bSelectedCustomerRequestCertificate: boolean = false
  bSelectedCustomerChangeMobile: boolean = false
  bSelectedCustomerChangeAddress: boolean = false
  bSelectedCustomerChangeEmail: boolean = false
  bSelectedCustomerFullMasterAuthority: boolean = false

  // CIF Level Authorities
  bSelectedCustomerViewOnly: boolean = false
  bSelectedCustomerDeactivateAccount: boolean = false
  bSelectedCustomerCanCreateFD: boolean = false
  bSelectedCustomerUnlimitedFDThreshold: boolean = false
  bSelectedCustomerCanMakeInternalTransfer: boolean = false
  bSelectedCustomerUnlimitedInternalTransferThreshold: boolean = false
  bSelectedCustomerCanMakeDhabiTransfer: boolean = false
  bSelectedCustomerUnlimitedDhabiTransferThreshold: boolean = false
  bSelectedCustomerCanMakeForeignTransfer: boolean = false
  bSelectedCustomerUnlimitedForeignTransferThreshold: boolean = false

  SelectedCustomerFDThreshold: number = 0
  bValidFDThreshold!: boolean

  SelectedCustomerInternalTransferThreshold: number = 0
  bValidInternalTxThreshold!: boolean

  SelectedCustomerDhabiTransferThreshold: number = 0
  bValidDhabiTxThreshold!: boolean

  SelectedCustomerForeignTransferThreshold: number = 0
  bValidForeignTxThreshold!: boolean

  readonly cifSupportedCurrencies: CifCurrencyCode[] = ['USD', 'EUR']
  readonly cifThresholdRows: Array<{ matrixId: number, label: string }> = [
    { matrixId: 1, label: 'Fixed Deposits' },
    { matrixId: 14, label: 'Internal Transfer' },
    { matrixId: 12, label: 'Transfer within Dhabi' },
    { matrixId: 13, label: 'Foreign Transfer' }
  ]
  cifThresholdState: { [matrixId: number]: { [currency: string]: CifThresholdCell } } = {}

  // Account Level Details
  companyAccounts: string[] = []
  accountCurrencyByNumber: { [accountNumber: string]: string } = {}
  SelectedCustomerAccount: string = ''
  SelectedCustomerAccountPermissions: any

  // Account grid state (drives the per-account permissions table)
  accountMatrixRows: any[] = []
  allAccountsSelected: boolean = false
  allFDSelected: boolean = false
  allInternalSelected: boolean = false
  allDhabiSelected: boolean = false
  allForeignSelected: boolean = false
  private readonly accountMatrixColumns: Array<{ key: 'fd' | 'internal' | 'dhabi' | 'foreign', matrixId: number }> = [
    { key: 'fd', matrixId: 1 },
    { key: 'internal', matrixId: 14 },
    { key: 'dhabi', matrixId: 12 },
    { key: 'foreign', matrixId: 13 }
  ]

  // Card Colors
  activeCardColor = { color: 'success', textColor: 'primary' }
  inactiveCardColor = { color: 'danger', textColor: 'secondary' }

  // Tab Controls
  profileTabActive = true
  matrixTabActive = false
  selectedTab = 0
  selectedNextTab = 0

  companyName: string = ''

  usersData: IItem[] = []
  selectedItems = [2, 3];

  // Search Form
  searchCif: string = ''
  cifSearchForm = new FormGroup({
    searchCifControl: new FormControl(this.searchCif, [Validators.required, Validators.minLength(3)])
  })

  // Customer Profile Form
  customerProfileForm!: FormGroup

  customerNameValid!: boolean
  isCustomerNameValid() {
    let returnVal = ''

    if (this.customerName?.invalid && (this.customerName?.dirty || this.customerName?.touched || !this.bNewCustomer)) {
      this.customerNameValid = false
      if (this.customerName?.errors?.['required'])
        returnVal = 'Name is required'
      else if (this.customerName?.errors?.['minlength'])
        returnVal = 'Name must be at least 2 characters long'
      else if (this.customerName?.errors?.['pattern'])
        returnVal = 'Only one space between words'
      else if (!this.customerName?.errors?.['maxLength'])
        returnVal = 'Name must be at least 60 characters long'
      else
        returnVal = 'Please enter a valid name'
    } else {
      var reg = new RegExp('^[a-zA-Z ]*$')
      if (!reg.test(this.customerName?.value)) {
        returnVal = 'You should only use alphabets (a to z)'
        this.customerNameValid = false
      } else {
        returnVal = ''
        if (this.customerName?.dirty || this.customerName?.touched) {
          this.customerNameValid = true
        }
      }
    }

    return returnVal
  }

  customerEmailValid!: boolean
  isCustomerEmailValid() {
    let returnVal = ''

    if (this.customerEmail?.invalid && (this.customerEmail?.dirty || this.customerEmail?.touched || !this.bNewCustomer)) {
      this.customerEmailValid = false
      if (this.customerEmail?.errors?.['required'])
        returnVal = 'Email is required'
      else if (this.customerEmail?.errors?.['pattern'])
        returnVal = 'Please enter a valid email ID'
      else
        returnVal = 'Please enter a valid email ID'
    } else {
      returnVal = ''
      if (this.customerEmail?.dirty || this.customerEmail?.touched) {
        this.customerEmailValid = true
      }
    }

    return returnVal
  }

  customerMobileValid!: boolean
  isCustomerMobileValid() {
    let returnVal = ''

    if (this.customerMobile?.invalid && (this.customerMobile?.dirty || this.customerMobile?.touched || !this.bNewCustomer)) {
      this.customerMobileValid = false
      if (this.customerMobile?.errors?.['required'])
        returnVal = 'Mobile number is required'
      else if (this.customerMobile?.value == undefined || (this.customerMobile?.value.substr(0, 1) !== '+' && this.customerMobile?.dirty)) {
        returnVal = 'Mobile number must start with + and country code'
        this.customerMobileValid = false
      }
      else if (this.customerMobile?.errors?.['pattern'])
        returnVal = 'Please enter a Number(0-9)'
      // else if (this.customerMobile?.value.indexOf('-') > -1 && this.customerMobile?.dirty){
      //     //console.log("why",this.customerMobile?.value.indexOf('-') > -1)
      //     returnVal =  'Mobile numbers not contain (-)'
      //     this.customerMobileValid = false
      //   }
      // else if (this.customerMobile?.errors?.['minlength'])
      //   returnVal = 'Mobile numbers must be 13 digits or more'
      else if (!this.customerMobile?.errors?.['maxLength'])
        returnVal = 'Mobile no must be at least 20 characters long'
      else
        returnVal = 'Please enter a valid mobile number(+9999999999999)'
    } else {
      if (this.customerMobile?.value == undefined || (this.customerMobile?.value.substr(0, 1) !== '+' && this.customerMobile?.dirty)) {
        returnVal = 'Mobile number must start with + and country code'
        this.customerMobileValid = false
      } else {
        returnVal = ''
        if (this.customerMobile?.dirty || this.customerMobile?.touched) {
          this.customerMobileValid = true
        }
      }
    }

    return returnVal
  }

  customerGenderValid!: boolean
  isCustomerGenderValid() {
    let returnVal = ''

    if (this.customeGender?.invalid && (this.customeGender?.dirty || this.customeGender?.touched || !this.bNewCustomer)) {
      this.customerGenderValid = false
      if (this.customeGender?.errors?.['required'])
        returnVal = 'Gender is required'
      else
        returnVal = 'Please select a valid Gender'
    } else {
      returnVal = ''
      if (this.customeGender?.dirty || this.customeGender?.touched) {
        this.customerGenderValid = true
      }
    }

    return returnVal
  }

  customerDOBValid!: boolean
  isCustomerDOBValid() {
    let returnVal = ''

    if (this.customeDateOfBirth?.invalid && (this.customeDateOfBirth?.dirty || this.customeDateOfBirth?.touched || !this.bNewCustomer)) {
      this.customerDOBValid = false
      if (this.customeDateOfBirth?.errors?.['required'])
        returnVal = 'Date of Birth is required'
      else
        returnVal = 'Please select a valid Date of Birth'
    } else {
      returnVal = ''
      if (this.customeDateOfBirth?.dirty || this.customeDateOfBirth?.touched) {
        this.customerDOBValid = true
      }
    }

    return returnVal
  }

  customerIDTypeValid!: boolean
  isCustomerIDTypeValid() {
    let returnVal = ''

    if (this.customerIDType?.invalid && (this.customerIDType?.dirty || this.customerIDType?.touched || !this.bNewCustomer)) {
      this.customerIDTypeValid = false
      if (this.customerIDType?.errors?.['required'])
        returnVal = 'ID Type is required'
      else
        returnVal = 'Please select a valid ID Type'
    } else {
      returnVal = ''
      if (this.customerIDType?.dirty || this.customerIDType?.touched) {
        this.customerIDTypeValid = true
      }
    }

    return returnVal
  }

  customerIDNumberValid!: boolean

  isCustomerIDNumberValid() {

    let returnVal = ''

    const value = this.customerIDNumber?.value || ''
    const idType = this.customerIDType?.value

    const passportRegex = /^[A-Za-z0-9]{5,9}$/ 

    if (this.customerIDNumber?.invalid && (this.customerIDNumber?.dirty || this.customerIDNumber?.touched || !this.bNewCustomer)) {
      
      this.customerIDNumberValid = false
      
      if (this.customerIDNumber?.errors?.['required']) {
        returnVal = 'ID number is required'
      }

    } else {

      // 👉 Emirates ID validation
      if (idType === 'Emirates ID' && this.customerIDNumber?.touched) {

        const emiratesRegex = /^784[0-9]{12}$/

        if (!/^[0-9]*$/.test(value)) {
          returnVal = 'Enter valid Emirates ID. e.g. 784xxxxxxxxxxxx'
          this.customerIDNumberValid = false
        }
        else if (value.length !== 15) {
          returnVal = 'Enter valid Emirates ID. e.g. 784xxxxxxxxxxxx'
          this.customerIDNumberValid = false
        }
        else if (!value.startsWith('784')) {
          returnVal = 'Emirates ID must start with 784xxxxxxxxxxxx'
          this.customerIDNumberValid = false
        }
        else if (!emiratesRegex.test(value)) {
          returnVal = 'Enter valid Emirates ID. e.g. 784xxxxxxxxxxxx'
          this.customerIDNumberValid = false
        }
        else {
          returnVal = ''
          this.customerIDNumberValid = true
        }

      }
      else if (idType === 'Passport' && this.customerIDNumber?.touched) {
        if (!passportRegex.test(value)) {
          returnVal = 'Enter a valid passport number (5–9 alphanumeric characters)'
          this.customerIDNumberValid = false
        }
        else {
          returnVal = ''
          this.customerIDNumberValid = true
        }
      }
      else {
        returnVal = ''
      }
    }
    return returnVal
  }  customerNationalityValid!: boolean
  isCustomerNationalityValid() {
    let returnVal = ''

    if (this.customerNationality?.invalid && (this.customerNationality?.dirty || this.customerNationality?.touched || !this.bNewCustomer)) {
      this.customerNationalityValid = false
      if (this.customerNationality?.errors?.['required'])
        returnVal = 'Nationality is required'
      else
        returnVal = 'Please select a valid nationality'
    } else {
      returnVal = ''
      if (this.customerNationality?.dirty || this.customerNationality?.touched) {
        this.customerNationalityValid = true
      }
    }

    return returnVal
  }

  customerIDExpiryValid!: boolean
  isCustomerIDExpiryValid() {
    let returnVal = ''

    if (this.customerIDExpiryDate?.errors?.['idExpired'] || (this.customerIDExpiryDate?.invalid && (this.customerIDExpiryDate?.dirty || this.customerIDExpiryDate?.touched))) {
      this.customerIDExpiryValid = false
      if (this.customerIDExpiryDate?.errors?.['required'])
        returnVal = 'ID Expiry date is required'
      else if (this.customerIDExpiryDate?.errors?.['idExpired'])
        returnVal = 'The ID has expired'
      else
        returnVal = 'Please select a valid ID Expiry Date'
    } else {
      returnVal = ''
      if (this.customerIDExpiryDate?.dirty || this.customerIDExpiryDate?.touched) {
        this.customerIDExpiryValid = true
      }
    }

    return returnVal
  }

  validateIDExpDate(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const today = new Date()
      let controlDate = new Date()
      let bInvalid = false

      if (this.selectedCustomerIDExpiresOn != null && this.selectedCustomerIDExpiresOn != undefined)
        controlDate = this.selectedCustomerIDExpiresOn

      if (controlDate.getTime() < today.getTime())
        bInvalid = true
      else
        bInvalid = false

      return bInvalid ? { 'idExpired': true } : null;
    };
  }

  // Customer Permissions Form
  customerPermissionsForm!: FormGroup

  ifFDValueValid() {
    if (isNaN(this.SelectedCustomerFDThreshold)) {
      console.log("Not a number: " + this.SelectedCustomerFDThreshold)
      return false
    }
    else {
      console.log("Is a number: " + this.SelectedCustomerFDThreshold)
      return true
    }
  }

  get customerName() { return this.customerProfileForm.get('customerName') }
  get customerEmail() { return this.customerProfileForm.get('customerEmail') }
  get customerMobile() { return this.customerProfileForm.get('customerMobile') }
  get customeGender() { return this.customerProfileForm.get('customeGender') }
  get customeDateOfBirth() { return this.customerProfileForm.get('customeDateOfBirth') }
  get customerIDType() { return this.customerProfileForm.get('customerIDType') }
  get customerIDNumber() { return this.customerProfileForm.get('customerIDNumber') }
  get customerNationality() { return this.customerProfileForm.get('customerNationality') }
  get customerIDExpiryDate() { return this.customerProfileForm.get('customerIDExpiryDate') }

  columns = [
    {
      key: 'cif',
      _style: { width: '7vw', minWidth: '7vw', maxWidth: '7vw' },
    },
    {
      key: 'name',
      _style: { width: '20vw', minWidth: '10vw', maxWidth: '30vw' },
    },
    'emailId',
    'mobileNumber',
    {
      key: 'approvalStatus',
      label: 'AML Status',
      _cellProps: { align: 'middle' },
      _style: { width: '7vw', minWidth: '7vw', maxWidth: '7vw' },
    },
    {
      key: 'createdOn',
      label: 'Created',
      _cellProps: { align: 'middle' },
      _style: { width: '20vw', minWidth: '10vw', maxWidth: '30vw' },
    },
    {
      key: 'isMaker',
      label: 'Role',
      _cellProps: { align: 'middle' },
      filter: false
    },
    {
      key: 'isCIFLevelAuthority',
      label: 'Authority Level',
      filter: false
    },
    {
      key: 'isApproved',
      label: 'Status',
      _cellProps: { align: 'middle' },
      filter: false
    }

  ];

  constructor(
    private changeDetector: ChangeDetectorRef,
    private restApi: RestService,
    private formatPipe: NumberFormatPipe,
    private datePipe: DatePipe) {

  }

  ngOnInit(): void {
    // Set minimum date of birth - 18 years of age
    let minAge = new Date()
    minAge.setFullYear(minAge.getFullYear() - 18)
    this.minDateOfBirth = minAge

    let minExpDate = new Date()
    minExpDate.setDate(minExpDate.getDate() + 1)
    this.minIDExpiryDate = minExpDate
    //console.log(this.minIDExpiryDate)
  }

  ngAfterContentChecked(): void {
    this.changeDetector?.detectChanges();
  }

  closeAlert(event: any) {
    this.bAlert = event.state

    if (event.data.action == 1) {
      if (event.data.matrixId == 15) {
        this.bSelectedCustomerViewOnly = false
      }
    } else if (event.data.action == 3) {
      this.resetMatrixControls()
      this.resetCommomPermissions()
      this.selectedItem.item.approvalMatrices = []
      this.SelectedCustomerAccountPermissions = []
      console.log(this.selectedItem)
      this.bSelectedCustomerCIFLevelAuthority = true
      this.selectedCustomerAuthorityLevel = 'CIF'
      this.authorityLevelTabLabel = 'CIF Level Authority'
      this.bCustomerRoleOrAuthorityChanged = true
    } else if (event.data.action == 4) {
      // Switch to Account: keep existing CIF-level and account-level matrices intact
      this.resetMatrixControls()
      this.resetCommomPermissions()
      this.SelectedCustomerAccountPermissions = this.selectedItem.item.approvalMatrices || []
      console.log(this.selectedItem)
      this.bSelectedCustomerCIFLevelAuthority = false
      this.selectedCustomerAuthorityLevel = 'Account'
      this.getCompanyAccounts(this.selectedItem.item.cif)
      this.bCustomerRoleOrAuthorityChanged = true
    } else if (event.data.action == 6) {
      this.resetMatrixControls()
      this.resetCommomPermissions()
      this.selectedItem.item.approvalMatrices = []
      this.SelectedCustomerAccountPermissions = []
      console.log(this.selectedItem)
      this.selectedCustomerRole = 'Maker'
      this.bIsSelectedCustomerMaker = true
      this.bCustomerRoleOrAuthorityChanged = true
    } else if (event.data.action == 7) {
      this.resetMatrixControls()
      this.resetCommomPermissions()
      this.selectedItem.item.approvalMatrices = []
      this.SelectedCustomerAccountPermissions = []
      console.log(this.selectedItem)
      this.selectedCustomerRole = 'Checker'
      this.bIsSelectedCustomerMaker = false
      this.bCustomerRoleOrAuthorityChanged = true
    } else if (event.data.action == 9) {

    }
  }

  alertVisibility(event: any) {
    this.bAlert = event.state
  }

  showAlertBox(heading: string, body: string, closeButtonText: string = "Close", showSecondButton: boolean = false, secondButtonText: string = "Understood", data: any = {}) {
    this.alertHeaderText = heading
    this.alertBodyText = body
    this.bShowSecondAlertButton = showSecondButton
    this.alertSecondButtonText = secondButtonText
    this.alertCloseButtonText = closeButtonText
    this.alertBoxData = data
    this.bAlert = true
  }

  onAlertButtonClick(event: any) {
    this.bAlert = event.state
    //console.log(event.data.action)

    if (event.data.action === 1) {
      console.log("event.data.action", 1)
      if (event.data.matrixId == 15) {
        console.log("event.data.action", this.bSelectedCustomerCIFLevelAuthority)
        this.bSelectedCustomerViewOnly = true
        if (!this.bSelectedCustomerCIFLevelAuthority)
          this.setAccountPermission(15)
        else
          this.setCIFPermission(15)
        console.log(this.bIsSelectedCustomerMaker, "test", this.SelectedCustomerAccount, "tt", this.bSelectedCustomerViewOnly)
        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.addOrUpdateAccountLevelMatrices(
            15,
            this.SelectedCustomerAccount,
            this.searchCif,
            this.bSelectedCustomerViewOnly,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.makeCIForAccountPermissionsReadonly()
        }
        //   else{
        //     this.addOrUpdateAccountLevelMatrices(
        //       16, 
        //       this.SelectedCustomerAccount, 
        //       this.searchCif, 
        //       this.bSelectedCustomerViewOnly, 
        //       0, 
        //       this.bIsSelectedCustomerMaker
        //     )
        //   console.log(this.bIsSelectedCustomerMaker,"test", this.SelectedCustomerAccount, "tt", this.bSelectedCustomerViewOnly)
        // }
      }
    } else if (event.data.action === 2) {

      // this.getAllRejectedCustomers();
      this.bShowCifSearchScreen = true
      this.bShowCustomerDetailsScreen = false
      this.resetMatrixControls()
      this.resetCommomPermissions()
      this.SelectedCustomerAccountPermissions = []
      this.selectedItem = []

      this.getCifDetails()
    } else if (event.data.action === 3) {
      this.bSelectedCustomerCIFLevelAuthority = false
      this.selectedCustomerAuthorityLevel = 'Account'
    } else if (event.data.action === 4) {
      this.bSelectedCustomerCIFLevelAuthority = true
      this.selectedCustomerAuthorityLevel = 'CIF'
    } else if (event.data.action === 5) {
      this.saveCustomer()
    } else if (event.data.action === 6) {
      this.selectedCustomerRole = 'Checker'
    } else if (event.data.action === 7) {
      this.selectedCustomerRole = 'Maker'
    } else if (event.data.action == 8) {
      this.activateOrDeactivateCustomer()
    } else if (event.data.action == 9) {
      this.activateOrDeactivateCustomer()
    }

  }

  OnCountryList(event: any) {
    if (event.success) {
      this.countryList = event.data
      if (this.selectedCustomerNationality) {
        const resolvedNationality = this.resolveNationalityShortCode(this.selectedCustomerNationality)
        this.selectedCustomerNationality = resolvedNationality
        this.customerNationality?.setValue(resolvedNationality)
      }
      console.log('Got country list')
    }
    else {
      this.showAlertBox('Sorry', 'Something wrong with the remote system. Please contact the support')
    }
  }

  private resolveNationalityShortCode(rawNationality: any): string {
    const fallback = (this.selectedCustomerNationality || 'AE').toUpperCase()
    const raw = (rawNationality ?? '').toString().trim()

    if (!raw) {
      return fallback
    }

    const rawUpper = raw.toUpperCase()
    const countries = this.countryList?.dhabiCountries || []

    const byShortCode = countries.find((country: any) =>
      (country?.shortCode || '').toUpperCase() === rawUpper
    )
    if (byShortCode?.shortCode) {
      return byShortCode.shortCode
    }

    const byName = countries.find((country: any) =>
      (country?.countryName || '').trim().toLowerCase() === raw.toLowerCase()
    )
    if (byName?.shortCode) {
      return byName.shortCode
    }

    return rawUpper
  }

  getCifDetails() {
    this.bViewOnly = true
    this.selectedSearchButton = 1
    this.searchBtnColor = 'primary'
    this.showRejectCustomerBtnColor = 'secondary'
    this.showAllRejectCustomerBtnColor = 'secondary'
    this.createCustomerBtnColor = 'secondary'

    if (this.cifSearchForm.controls['searchCifControl'].value != null) {
      this.searchCif = this.cifSearchForm.controls['searchCifControl'].value
    }

    if (this.searchCif === '') {
      this.showAlertBox('Oops!!', 'You need to enter a valid CIF')

      return
    }

    this.bLoading = true

    this.restApi.getCifDetails({ cif: this.searchCif }).subscribe({
      next: (v: CifDetails) => {
        if (v.success) {
          if (v.customerDetailsRes.responseCode === "000") {
            if (v.customerDetailsRes.ciftype === "COMPANY") {
              this.companyId = v.companyId
              this.companyName = v.customerDetailsRes.name1 + ', ' + v.customerDetailsRes.street + ', ' + v.customerDetailsRes.addressLine2 + ', ' + v.customerDetailsRes.towncountry + ', ' + v.customerDetailsRes.country + ', ' + v.customerDetailsRes.postcode
              this.getCustomers(this.searchCif)
            }
            else {
              this.bLoading = false
              this.getCustomers(this.searchCif);
              this.showAlertBox("Error", "The CIF " + this.searchCif + " is not a valid company. Please try with other CIF number!")
            }
          }
        }
        else {
          if (v.customerDetailsRes.responseCode === "0025") {
            this.bLoading = false
            this.showAlertBox("Error", "No customers found for the provided CIF.")
          }
          else {
            this.bLoading = false
            this.showAlertBox("Error", "Oops! There was an error getting data from Middleware. Have patience and try after some time.")
          }
        }

      },
      error: (e) => {
        this.bLoading = false
        this.showAlertBox("Error", "Oops! Something went wrong. Have patience and try after some time.")
      },
    })
  }

  getCustomers(Cif: string) {
    this.restApi.getCorporateCustomers({ cif: this.searchCif }).subscribe({
      next: (v: CorporateUserDetails) => {
        this.bLoading = false
        this.usersData = v.corporateUsers.map((item, UserId) => {
          const _selected = this.selectedItems.includes(UserId);
          return {
            ...item,
            _selected
          };
        })
      },
      error: (e) => {
        this.bLoading = false
        this.showAlertBox("Error", "Oops! Something went wrong. Have patience and try after some time.")
      }
    })

  }

  getCompanyAccounts(Cif: string) {
    console.log('Selected CIF: ' + this.selectedItem.item.cif)

    this.restApi.getCompanyAccounts({ cif: this.selectedItem.item.cif }).subscribe({
      next: (v: GetCompanyAccounts) => {
        const normalized = this.normalizeCompanyAccounts(v.accounts)
        this.companyAccounts = normalized.accountNumbers
        this.accountCurrencyByNumber = normalized.currencyByAccount
        console.log(this.companyAccounts);
        if (this.companyAccounts) {
          this.getFirstValidAccount()
        }
        this.authorityLevelTabLabel = 'Account Level Authorities'
        this.buildAccountMatrixGrid()
      },
      error: (e) => {
        this.showAlertBox('Sorry', 'Unable to retrieve account details of this company. Please contact support.')
      }
    })
  }

  private normalizeCompanyAccounts(accounts: GetCompanyAccounts['accounts']): { accountNumbers: string[], currencyByAccount: { [accountNumber: string]: string } } {
    const currencyByAccount: { [accountNumber: string]: string } = {}

    if (Array.isArray(accounts)) {
      accounts.forEach((accountNumber: string) => {
        currencyByAccount[accountNumber] = 'USD'
      })
      return {
        accountNumbers: accounts,
        currencyByAccount
      }
    }

    Object.entries(accounts || {}).forEach(([accountNumber, currency]) => {
      currencyByAccount[accountNumber] = (currency || 'USD').toUpperCase()
    })

    return {
      accountNumbers: Object.keys(currencyByAccount),
      currencyByAccount
    }
  }

  getCustomerProfile() {
    this.restApi.getUserProfile(this.selectedItem.item.userId).subscribe({
      next: (v: UserProfile) => {
        console.log("Got User Profile")

        this.approvedCustomerProfile = v
        this.isNew = v.isNew;
        if (v.isNew == true) {
          this.bHasCustomerMatrixApprovalPending = false;
          this.showApprovedOrPendingScreen(false);
        }
        this.customerName?.setValue(v.name)
        this.customerEmail?.setValue(v.emailId)
        this.customerMobile?.setValue(v?.mobileNumber)
        this.customeGender?.setValue(v.genderId == 1 ? 'Male' : 'Female')
        this.customeDateOfBirth?.setValue(new Date(v.dateOfBirth))
        this.customerIDType?.setValue(v.isEID ? 'Emirates ID' : 'Passport')
        this.customerIDNumber?.setValue(v.eidNumber)
        const resolvedNationality = this.resolveNationalityShortCode(v.nationalityCountryCode)
        this.selectedCustomerNationality = resolvedNationality
        this.customerNationality?.setValue(resolvedNationality)
        this.customerIDExpiryDate?.setValue(new Date(v.expiresOn))

        this.selectedCustomerDateOfBirth = new Date(v.dateOfBirth)
        this.selectedCustomerIDExpiresOn = new Date(v.expiresOn)

        if (this.approvedCustomerProfile.name != this.approvedCustomerProfile.oldName)
          this.bApprovedName = true

        if (this.approvedCustomerProfile.emailId != this.approvedCustomerProfile.oldEmailId)
          this.bApprovedEmail = true

        if (this.approvedCustomerProfile.mobileNumber != this.approvedCustomerProfile.oldMobileNumber)
          this.bApprovedMobile = true

        if (this.approvedCustomerProfile.genderId != this.approvedCustomerProfile.oldGenderId)
          this.bApprovedGender = true

        if (this.approvedCustomerProfile.dateOfBirth != this.approvedCustomerProfile.oldDateOfBirth)
          this.bApprovedDOB = true

        if (this.approvedCustomerProfile.isEID != this.approvedCustomerProfile.wasEID)
          this.bApprovedIDType = true

        if (this.approvedCustomerProfile.eidNumber != this.approvedCustomerProfile.oldEIDNumber)
          this.bApprovedIDNumber = true

        if (this.approvedCustomerProfile.nationalityCountryCode != this.approvedCustomerProfile.oldNationalityCountryCode)
          this.bApprovedNationality = true

        if (this.approvedCustomerProfile.expiresOn != this.approvedCustomerProfile.oldExpiresOn)
          this.bApprovedIDExpiry = true

        this.bShowingApprovedName = true
        this.bShowingApprovedDOB = true
        this.bShowingApprovedEmail = true
        this.bShowingApprovedGender = true
        this.bShowingApprovedIDExpiry = true
        this.bShowingApprovedIDNumber = true
        this.bShowingApprovedIDType = true
        this.bShowingApprovedMobile = true
        this.bShowingApprovedNationaility = true

        this.customerNameValid = this.bUndefined
        this.customerEmailValid = this.bUndefined
        this.customerMobileValid = this.bUndefined
        this.customerGenderValid = this.bUndefined
        this.customerDOBValid = this.bUndefined
        this.customerIDTypeValid = this.bUndefined
        this.customerIDNumberValid = this.bUndefined
        this.customerNationalityValid = this.bUndefined
        this.customerIDExpiryValid = this.bUndefined
      },
      error: (e) => {

      }
    })
  }

  bApprovedName: boolean = false
  bShowingApprovedName: boolean = true

  bApprovedEmail: boolean = false
  bShowingApprovedEmail: boolean = true

  bApprovedMobile: boolean = false
  bShowingApprovedMobile: boolean = true

  bApprovedGender: boolean = false
  bShowingApprovedGender: boolean = true

  bApprovedDOB: boolean = false
  bShowingApprovedDOB: boolean = true

  bApprovedIDType: boolean = false
  bShowingApprovedIDType: boolean = true

  bApprovedIDNumber: boolean = false
  bShowingApprovedIDNumber: boolean = true

  bApprovedNationality: boolean = false
  bShowingApprovedNationaility: boolean = true

  bApprovedIDExpiry: boolean = false
  bShowingApprovedIDExpiry: boolean = true

  showApprovedProfile(event: any, controlId: number) {
    event.stopPropagation()
    event.preventDefault()

    if (this.approvedCustomerProfile != null) {
      switch (controlId) {
        case 1:
          this.bShowingApprovedName = !this.bShowingApprovedName

          if (this.bShowingApprovedName)
            this.customerName?.setValue(this.selectedItem.item.name)
          else
            this.customerName?.setValue(this.approvedCustomerProfile.oldName)

          this.customerNameValid = this.bUndefined
          break;
        case 2:
          this.bShowingApprovedEmail = !this.bShowingApprovedEmail

          if (this.bShowingApprovedEmail)
            this.customerEmail?.setValue(this.selectedItem.item.emailId)
          else
            this.customerEmail?.setValue(this.approvedCustomerProfile.oldEmailId)

          this.customerEmailValid = this.bUndefined
          break;
        case 3:
          this.bShowingApprovedMobile = !this.bShowingApprovedMobile

          if (this.bShowingApprovedMobile)
            this.customerMobile?.setValue(this.approvedCustomerProfile.mobileNumber)
          else
            this.customerMobile?.setValue(this.approvedCustomerProfile.oldMobileNumber)

          this.customerMobileValid = this.bUndefined
          break;
        case 4:
          this.bShowingApprovedGender = !this.bShowingApprovedGender

          if (this.bShowingApprovedGender)
            this.customeGender?.setValue(this.approvedCustomerProfile.genderId == 1 ? 'Male' : 'Female')
          else
            this.customeGender?.setValue(this.approvedCustomerProfile.oldGenderId == 1 ? 'Male' : 'Female')

          this.customerGenderValid = this.bUndefined
          break;
        case 5:
          this.bShowingApprovedDOB = !this.bShowingApprovedDOB

          if (this.bShowingApprovedDOB)
            this.customeDateOfBirth?.setValue(this.approvedCustomerProfile.DateOfBirth)
          else
            this.customeDateOfBirth?.setValue(this.approvedCustomerProfile.oldDateOfBirth)

          this.customerDOBValid = this.bUndefined
          break;
        case 6:
          this.bShowingApprovedIDType = !this.bShowingApprovedIDType

          if (this.bShowingApprovedIDType)
            this.customerIDType?.setValue(this.approvedCustomerProfile.isEID ? 'Emirates ID' : 'Passport')
          else
            this.customerIDType?.setValue(this.approvedCustomerProfile.wasEID ? 'Emirates ID' : 'Passport')

          this.customerIDTypeValid = this.bUndefined
          break;
        case 7:
          this.bShowingApprovedIDNumber = !this.bShowingApprovedIDNumber

          if (this.bShowingApprovedIDNumber)
            this.customerIDNumber?.setValue(this.approvedCustomerProfile.eidNumber)
          else
            this.customerIDNumber?.setValue(this.approvedCustomerProfile.oldEIDNumber)

          this.customerIDNumberValid = this.bUndefined
          break;
        case 8:
          this.bShowingApprovedNationaility = !this.bShowingApprovedNationaility

          if (this.bShowingApprovedNationaility)
            this.customerNationality?.setValue(this.resolveNationalityShortCode(this.approvedCustomerProfile.nationalityCountryCode))
          else
            this.customerNationality?.setValue(this.resolveNationalityShortCode(this.approvedCustomerProfile.oldNationalityCountryCode))

          this.customerNationalityValid = this.bUndefined
          break;
        case 9:
          this.bShowingApprovedIDExpiry = !this.bShowingApprovedIDExpiry

          if (this.bShowingApprovedIDExpiry)
            this.customerIDExpiryDate?.setValue(this.approvedCustomerProfile.expiresOn)
          else
            this.customerIDExpiryDate?.setValue(this.approvedCustomerProfile.oldExpiresOn)

          this.customerIDExpiryValid = this.bUndefined
          break;
      }

    }
  }

  rowSelected = (clickedItem: any) => {
    console.log(clickedItem.item)
    this.bViewOnly = true
    if (clickedItem.item.rejectionReason != undefined && clickedItem.item.rejectionReason != null && !this.bNewCustomer) { this.rejectionReason = clickedItem.item.rejectionReason }
    else if (clickedItem.item.approvalMatrices != "") { this.rejectionReason = clickedItem.item.approvalMatrices[0].disapprovalReason }


    // Don't proceed if the active/inactive decision is pending
    if (clickedItem.item.isActiveDeactiveRequestPending) {
      this.showAlertBox('Sorry!', 'This customer is pending action from Checker for activation or deactivation.');
      return;
    }

    // Don't proceed if the customer is inactive

    // if (clickedItem.item.isDeactivated) {
    //   this.selectedItem = clickedItem;
    //   this.showAlertBox('Please confirm!', 'This customer is currently INACTIVE. Do you want to request for ACTIVATION?', 'No, Thanks', true, 'Yes', { action: 9, matrixId: 0 });
    //   return;
    // }

    this.selectedCustomerAuthorityLevel = 'CIF'
    this.bNewCustomer = false

    this.bApprovedName = false
    this.bShowingApprovedName = false

    this.bApprovedEmail = false
    this.bShowingApprovedEmail = true

    this.bApprovedMobile = false
    this.bShowingApprovedMobile = false

    this.bApprovedGender = false
    this.bShowingApprovedGender = false

    this.bApprovedDOB = false
    this.bShowingApprovedDOB = false

    this.bApprovedIDType = false
    this.bShowingApprovedIDType = false

    this.bApprovedIDNumber = false
    this.bShowingApprovedIDNumber = false

    this.bApprovedNationality = false
    this.bShowingApprovedNationaility = false

    this.bApprovedIDExpiry = false
    this.bShowingApprovedIDExpiry = false

    this.resetCommomPermissions()
    this.resetMatrixControls()

    this.bSelectedCustomerCIFLevelAuthority = false
    this.bIsSelectedCustomerMaker = false
    this.bSelectedCustomerCreateSB = false
    this.bSelectedCustomerCreateCurrent = false
    this.bSelectedCustomerRequestCertificate = false
    this.bSelectedCustomerChangeMobile = false
    this.bSelectedCustomerChangeAddress = false
    this.bSelectedCustomerChangeEmail = false
    this.bSelectedCustomerFullMasterAuthority = false

    // CIF Level Authorities
    this.bSelectedCustomerViewOnly = false
    this.bSelectedCustomerDeactivateAccount = false
    this.bSelectedCustomerCanCreateFD = false
    this.bSelectedCustomerUnlimitedFDThreshold = false
    this.bSelectedCustomerCanMakeInternalTransfer = false
    this.bSelectedCustomerUnlimitedInternalTransferThreshold = false
    this.bSelectedCustomerCanMakeDhabiTransfer = false
    this.bSelectedCustomerUnlimitedDhabiTransferThreshold = false
    this.bSelectedCustomerCanMakeForeignTransfer = false
    this.bSelectedCustomerUnlimitedForeignTransferThreshold = false

    // Account Level Details
    this.SelectedCustomerAccount = ''

    this.selectedTab = 0
    this.profileTabActive = true
    this.matrixTabActive = false
    this.bHasCustomerMatrixApprovalPending = false

    this.SelectedCustomerAccountPermissions = []

    this.bCustomerPermissionsEdited = false
    this.bCustomerProfileEdited = false

    this.selectedItem = clickedItem
    // Keep a deep copy
    this.originalCustomerDetails = _.cloneDeep(clickedItem)
    console.log(this.selectedItem)
    this.bUserActive = this.selectedItem.item.isActive
    this.bUserRejected = this.selectedItem.item.isApproved
    //console.log(this.bUserActive, this.bUserRejected)
    if (this.selectedItem.item.approvalMatrices != undefined) {
      this.selectedItem.item.approvalMatrices.every((element: any) => {
        if (!element.isDecisionTaken) {
          console.log("got isDecision as false")
          console.log(element)
          this.bHasCustomerMatrixApprovalPending = true
          return false
        }

        return true
      })
    }

    this.selectedCustomerAuthorityLevel = this.getSelectedAuthorityLevel()
    this.selectedCustomerNationality = this.resolveNationalityShortCode(this.selectedItem.item.nationalityCountryCode)

    this.bShowCifSearchScreen = false
    this.bShowCustomerDetailsScreen = true

    this.selectedCustomerDateOfBirth = new Date(this.selectedItem.item.dateOfBirth)
    this.selectedCustomerIDExpiresOn = new Date(this.selectedItem.item.expiresOn)
    this.selectedCustomerGender = this.getSelectedGender()
    this.selectedCustomerIDType = this.getSelectedIDType()
    this.selectedCustomerRole = this.getSelectedRole()

    this.customerProfileForm = new FormGroup({
      customerName: new FormControl(this.selectedItem.item.name, [Validators.required, Validators.minLength(2), Validators.pattern(/^((?!\s{2,}).)*$/), Validators.maxLength(60)]),
      customerEmail: new FormControl(this.selectedItem.item.emailId, [Validators.required, Validators.pattern(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)]),
      customerMobile: new FormControl(this.selectedItem.item.mobileNumber, [Validators.required, Validators.minLength(0), Validators.maxLength(20), Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')]),
      customeGender: new FormControl(this.selectedCustomerGender, [Validators.required]),
      customeDateOfBirth: new FormControl(this.selectedCustomerDateOfBirth, [Validators.required]),
      customerIDType: new FormControl(this.selectedCustomerIDType, [Validators.required]),
      customerIDNumber: new FormControl(this.selectedItem.item.eidNumber, [Validators.required, Validators.minLength(5)]),
      customerNationality: new FormControl(this.selectedCustomerNationality, [Validators.required]),
      customerIDExpiryDate: new FormControl(this.selectedCustomerIDExpiresOn, [Validators.required, this.validateIDExpDate()])
    })

    if (this.bViewOnly)
      this.customerProfileForm.disable()

    this.customerProfileForm.valueChanges.subscribe(x => {
      this.bCustomerProfileEdited = true
    })

    this.customerNameValid = this.bUndefined
    this.customerEmailValid = this.bUndefined
    this.customerMobileValid = this.bUndefined
    this.customerGenderValid = this.bUndefined
    this.customerDOBValid = this.bUndefined
    this.customerIDTypeValid = this.bUndefined
    this.customerIDNumberValid = this.bUndefined
    this.customerNationalityValid = this.bUndefined
    this.customerIDExpiryValid = this.bUndefined

    if (!this.selectedItem.item.isDecisionTaken)
      this.getCustomerProfile()

    if (this.selectedSearchButton == 2 || this.selectedSearchButton == 3) {
      this.isCustomerRejected = true
      if (this.selectedItem.item.rejectionReason != undefined && this.selectedItem.item.rejectionReason != null)
        this.rejectionReason = this.selectedItem.item.rejectionReason
      else if (this.selectedItem.item.approvalMatrices != undefined && this.selectedItem.item.approvalMatrices[0] != undefined && this.selectedItem.item.approvalMatrices[0].disapprovalReason != null && this.selectedItem.item.approvalMatrices[0].disapprovalReason != undefined)
        this.rejectionReason = this.selectedItem.item.approvalMatrices[0].disapprovalReason
    } else {
      this.isCustomerRejected = false
    }
  }

  showApprovedOrPendingScreen(value: boolean) {
    this.bShowingApprovedData = value
    console.log(this.bShowingApprovedData)

    if (value)
      this.topScreenMessage = 'You are looking at the approved permissions of this customer. Click to see the permissions pending approval.'
    else
      this.topScreenMessage = 'Now showing only the permissions which are pending approval. Click to see the approved permissions.'

    this.reloadCustomer()
  }

  enableDisableProfileForm(state: boolean) {
    if (state)
      this.customerProfileForm.enable()
    else
      this.customerProfileForm.disable()
  }

  getSelectedRole() {
    if (this.selectedItem.item.approvalMatrices != undefined && this.selectedItem.item.approvalMatrices.length != 0) {
      this.bIsSelectedCustomerAuthorityDefined = true

      var tempRole = ''

      this.selectedItem.item.approvalMatrices.every((element: any) => {
        if ((this.bShowingApprovedData && element.isDecisionTaken) || (!this.bShowingApprovedData && !element.isDecisionTaken)) {
          // First see if there 'enable' requests
          if (element.enable && element.isMaker) {
            tempRole = 'Maker'
            return false
          }
          else if (element.enable && !element.isMaker) {
            tempRole = 'Checker'
            return false
          }

          if (element.isMaker)
            tempRole = 'Maker'
          else if (!element.isMaker)
            tempRole = 'Checker'
        } else {
          // First see if there 'enable' requests
          if (!element.enable && element.isMaker) {
            tempRole = 'Maker'
            return false
          }
          else if (!element.enable && !element.isMaker) {
            tempRole = 'Checker'
            return false
          }

          if (element.isMaker)
            tempRole = 'Maker'
          else if (!element.isMaker)
            tempRole = 'Checker'
        }


        return true
      })

      if (tempRole === 'Maker')
        this.bIsSelectedCustomerMaker = true
      else
        this.bIsSelectedCustomerMaker = false

      return tempRole
    }

    this.bIsSelectedCustomerAuthorityDefined = false

    this.showAlertBox("Warning", "This customer does not have any authority matrix defined")

    return ''
  }

  getSelectedAuthorityLevel() {
    let authorityLevel = " "

    if (this.selectedItem.item.approvalMatrices != undefined && this.selectedItem.item.approvalMatrices.length != 0) {
      this.bIsSelectedCustomerAuthorityDefined = true

      // Now get the Master Authorities
      this.selectedItem.item.approvalMatrices.forEach((element: any) => {
        if ((this.bShowingApprovedData && element.isDecisionTaken) || (!this.bShowingApprovedData && !element.isDecisionTaken)) {
          switch (element.matrixId) {
            case 2:
              this.bSelectedCustomerCreateSB = element.enable
              break;
            case 3:
              this.bSelectedCustomerCreateCurrent = element.enable
              break;
            case 4:
              this.bSelectedCustomerChangeAddress = element.enable
              break;
            case 5:
              this.bSelectedCustomerChangeMobile = element.enable
              break;
            case 6:
              this.bSelectedCustomerChangeEmail = element.enable
              break;
            case 9:
              this.bSelectedCustomerRequestCertificate = element.enable
              break;
          }
        }
      });

      if (this.bSelectedCustomerChangeAddress &&
        this.bSelectedCustomerChangeEmail &&
        this.bSelectedCustomerChangeMobile &&
        this.bSelectedCustomerCreateCurrent &&
        this.bSelectedCustomerCreateSB &&
        this.bSelectedCustomerRequestCertificate)
        this.bSelectedCustomerFullMasterAuthority = true

      //console.log("Authority Level: " + this.selectedItem.item.approvalMatrices[0].isCIFLevelAuthority)

      this.selectedItem.item.approvalMatrices.every((element: any) => {
        if (this.bShowingApprovedData && this.bHasCustomerMatrixApprovalPending) {
          if (!element.enable && element.isCIFLevelAuthority) {
            authorityLevel = 'CIF'
            return false
          }
          else if (!element.enable && !element.isCIFLevelAuthority) {
            authorityLevel = 'Account'
            return false
          }

          if (element.isCIFLevelAuthority) {
            authorityLevel = 'CIF'
          }
          else if (!element.isCIFLevelAuthority) {
            authorityLevel = 'Account'
          }
        } else {
          if (element.enable && element.isCIFLevelAuthority) {
            authorityLevel = 'CIF'
            return false
          }
          else if (element.enable && !element.isCIFLevelAuthority) {
            authorityLevel = 'Account'
            return false
          }

          if (element.isCIFLevelAuthority) {
            authorityLevel = 'CIF'
          }
          else if (!element.isCIFLevelAuthority) {
            authorityLevel = 'Account'
          }
        }
        return true
      })

      if (authorityLevel == 'CIF') {
        this.bSelectedCustomerCIFLevelAuthority = true
        this.authorityLevelTabLabel = 'CIF Level Authorities'
      } else {

        this.bSelectedCustomerCIFLevelAuthority = false
        this.SelectedCustomerAccountPermissions = this.selectedItem.item.approvalMatrices
        this.getCompanyAccounts(this.selectedItem.item.cif)
      }
      console.log(this.bSelectedCustomerCIFLevelAuthority)
      if (this.bSelectedCustomerCIFLevelAuthority) {
        this.resetCifThresholdState()

        // Now get the CIF Level Authorities
        this.selectedItem.item.approvalMatrices.forEach((element: any) => {
          if ((this.bShowingApprovedData && element.isDecisionTaken) || (!this.bShowingApprovedData && !element.isDecisionTaken)) {
            switch (element.matrixId) {
              case 15:
                this.bSelectedCustomerViewOnly = element.enable
                break;
              case 16:
                this.bSelectedCustomerDeactivateAccount = element.enable
                break;
              case 1:
                this.setCifThresholdFromMatrix(element)

                break;
              case 14:
                this.setCifThresholdFromMatrix(element)
                break;
              case 12:
                this.setCifThresholdFromMatrix(element)

                break;
              case 13:
                this.setCifThresholdFromMatrix(element)

                break;
            }
          }
        });

        this.syncLegacyCifThresholdFromDefaultCurrency()
      }
      else {
        // Get the first account and set it
        this.getFirstValidAccount()
      }

      return authorityLevel
    }

    this.bIsSelectedCustomerAuthorityDefined = false

    return ''
  }
  val: any = [];
  getFirstValidAccount() {
    //console.log(this.selectedItem.item.approvalMatrices)
    if (this.selectedItem.item.approvalMatrices != undefined) {
      // Initially set this
      this.val = this.selectedItem.item.approvalMatrices
      this.SelectedCustomerAccount = this.companyAccounts[0];
      this.authorityLevelTabLabel = 'Permissions For Account : ' + this.SelectedCustomerAccount
      this.selectedItem.item.approvalMatrices.forEach((element: any) => {

        if (element.accountNumber != undefined && element.accountNumber != '') {

          if (this.bShowingApprovedData == false && element.isDecisionTaken == false) {
            this.SelectedCustomerAccount = element.accountNumber
          }
          if (this.bShowingApprovedData == true && element.isDecisionTaken == true) {
            this.SelectedCustomerAccount = element.accountNumber
          }


          this.setAccountMatrices()
          this.authorityLevelTabLabel = 'Permissions For Account : ' + element.accountNumber
          return
        }
      });


    }
  }

  private isThresholdCIFMatrix(matrixId: number) {
    return matrixId === 1 || matrixId === 12 || matrixId === 13 || matrixId === 14
  }

  private normalizeCurrencyCode(currencyCode: string | undefined | null): CifCurrencyCode {
    const normalized = (currencyCode || this.cifSupportedCurrencies[0]).toUpperCase() as CifCurrencyCode
    if (this.cifSupportedCurrencies.includes(normalized)) {
      return normalized
    }
    return this.cifSupportedCurrencies[0]
  }

  private getDefaultCifThresholdCell(): CifThresholdCell {
    return {
      threshold: 0,
      unlimited: false,
      valid: this.bUndefined
    }
  }

  private ensureCifThresholdState() {
    this.cifThresholdRows.forEach((row) => {
      if (!this.cifThresholdState[row.matrixId]) {
        this.cifThresholdState[row.matrixId] = {}
      }

      this.cifSupportedCurrencies.forEach((currency) => {
        if (!this.cifThresholdState[row.matrixId][currency]) {
          this.cifThresholdState[row.matrixId][currency] = this.getDefaultCifThresholdCell()
        }
      })
    })
  }

  private resetCifThresholdState() {
    this.cifThresholdState = {}
    this.ensureCifThresholdState()
  }

  getCifThresholdCell(matrixId: number, currencyCode: string): CifThresholdCell {
    this.ensureCifThresholdState()
    const currency = this.normalizeCurrencyCode(currencyCode)
    return this.cifThresholdState[matrixId][currency]
  }

  getCifMatrixEnabled(matrixId: number): boolean {
    switch (matrixId) {
      case 1:
        return this.bSelectedCustomerCanCreateFD
      case 12:
        return this.bSelectedCustomerCanMakeDhabiTransfer
      case 13:
        return this.bSelectedCustomerCanMakeForeignTransfer
      case 14:
        return this.bSelectedCustomerCanMakeInternalTransfer
      default:
        return false
    }
  }

  isAllCifThresholdAuthoritiesEnabled(): boolean {
    return this.cifThresholdRows.every((row) => this.getCifMatrixEnabled(row.matrixId))
  }

  private setCifMatrixEnabled(matrixId: number, enabled: boolean) {
    switch (matrixId) {
      case 1:
        this.bSelectedCustomerCanCreateFD = enabled
        break
      case 12:
        this.bSelectedCustomerCanMakeDhabiTransfer = enabled
        break
      case 13:
        this.bSelectedCustomerCanMakeForeignTransfer = enabled
        break
      case 14:
        this.bSelectedCustomerCanMakeInternalTransfer = enabled
        break
    }
  }

  private setCifThresholdFromMatrix(element: any) {
    if (!this.isThresholdCIFMatrix(element.matrixId)) {
      return
    }

    const currency = this.normalizeCurrencyCode(element.currencyCode || element.currency)
    const cell = this.getCifThresholdCell(element.matrixId, currency)
    const isUnlimited = element.threshold == -1

    cell.unlimited = isUnlimited
    cell.threshold = isUnlimited ? 0 : (element.threshold || 0)
    cell.valid = this.bUndefined
    this.setCifMatrixEnabled(element.matrixId, this.getCifMatrixEnabled(element.matrixId) || !!element.enable)
  }

  private syncLegacyCifThresholdFromDefaultCurrency() {
    const defaultCurrency = this.cifSupportedCurrencies[0]

    const fdCell = this.getCifThresholdCell(1, defaultCurrency)
    this.SelectedCustomerFDThreshold = fdCell.threshold
    this.bSelectedCustomerUnlimitedFDThreshold = fdCell.unlimited
    this.bValidFDThreshold = fdCell.valid

    const internalCell = this.getCifThresholdCell(14, defaultCurrency)
    this.SelectedCustomerInternalTransferThreshold = internalCell.threshold
    this.bSelectedCustomerUnlimitedInternalTransferThreshold = internalCell.unlimited
    this.bValidInternalTxThreshold = internalCell.valid

    const dhabiCell = this.getCifThresholdCell(12, defaultCurrency)
    this.SelectedCustomerDhabiTransferThreshold = dhabiCell.threshold
    this.bSelectedCustomerUnlimitedDhabiTransferThreshold = dhabiCell.unlimited
    this.bValidDhabiTxThreshold = dhabiCell.valid

    const foreignCell = this.getCifThresholdCell(13, defaultCurrency)
    this.SelectedCustomerForeignTransferThreshold = foreignCell.threshold
    this.bSelectedCustomerUnlimitedForeignTransferThreshold = foreignCell.unlimited
    this.bValidForeignTxThreshold = foreignCell.valid
  }

  switchAuthorityLevel(event: any) {
    if (this.selectedCustomerAuthorityLevel == 'CIF') {
      this.showAlertBox('Warning', "If you switch to CIF level authority, all other permissions of this customer will be reset. Are you sure you want to do this?",
        "Yes, I am quite sure", true, "No, I don't want to", { action: 3, matrixId: 0 })
    } else {
      this.showAlertBox('Warning', "If you switch to Account level authority, all other permissions of this customer will be reset. Are you sure you want to do this?",
        "Yes, I am quite sure", true, "No, I don't want to", { action: 4, matrixId: 0 })
    }
  }

  // switchRole(event: any) {
  //   console.log(this.bSelectedCustomerCreateSB)
  //   if (this.selectedCustomerRole == 'Maker') {
  //     // this.showAlertBox('Warning', "If you switch to Maker, all the permissions of this customer will be reset. Are you sure you want to do this?",
  //     //   "Yes, I am quite sure", true,  "No, I don't want to", {action: 7, matrixId: 0})
  //       // this.showAlertBox(
  //       //   'Warning', 
  //       //   'If you switch to Maker, all the permissions of this customer will be reset. Are you sure you want to do this?', 
  //       //   "No, I don't want to", true, 'Yes, I am quite sure',
  //       //   {action: 6, matrixId: 0})

  //   } else {
  //     // this.showAlertBox('Warning', "If you switch to Cheker, all the permissions of this customer will be reset. Are you sure you want to do this?",
  //     //   "Yes, I am quite sure", true,  "No, I don't want to", {action: 6, matrixId: 0})
  //     // this.showAlertBox(
  //     //   'Warning', 
  //     //   'If you switch to Cheker, all the permissions of this customer will be reset. Are you sure you want to do this?', 
  //     //   "No, I don't want to", true, 'Yes, I am quite sure',
  //     //   {action: 7, matrixId: 0})
  //   }
  // }
  switchRole(event: any) {
    if (this.selectedCustomerRole == 'Maker') {
      this.showAlertBox('Warning', "If you switch to Maker, all the permissions of this customer will be reset. Are you sure you want to do this?",
        "Yes, I am quite sure", true, "No, I don't want to", { action: 6, matrixId: 0 })
    } else {
      this.showAlertBox('Warning', "If you switch to Cheker, all the permissions of this customer will be reset. Are you sure you want to do this?",
        "Yes, I am quite sure", true, "No, I don't want to", { action: 7, matrixId: 0 })
    }
  }
  getDateDisplayString(item: any) {
    let displayDate = '';
    if (item.approvalMatrices != undefined && item.approvalMatrices[0] != undefined) {
      let displayDate = this.datePipe.transform(item.approvalMatrices[0].createdOn, "dd-MM-yyyy")
      if (item.approvalMatrices[0].createdOn) {

        return displayDate
      } else {
        return displayDate
      }
    } else {
      return displayDate
      console.log(item)
    }
  }
  onChangeAccount() {
    this.bSelectedCustomerViewOnly = false
    this.setAccountMatrices()
    this.authorityLevelTabLabel = 'Permissions For Account : ' + this.SelectedCustomerAccount
  }

  resetMatrixControls() {

    this.bViewOnly = true
    this.bSelectedCustomerViewOnly = false
    this.bSelectedCustomerDeactivateAccount = false
    this.bSelectedCustomerCanCreateFD = false
    this.bSelectedCustomerUnlimitedFDThreshold = false
    this.bSelectedCustomerCanMakeInternalTransfer = false
    this.bSelectedCustomerUnlimitedInternalTransferThreshold = false
    this.bSelectedCustomerCanMakeDhabiTransfer = false
    this.bSelectedCustomerUnlimitedDhabiTransferThreshold = false
    this.bSelectedCustomerCanMakeForeignTransfer = false
    this.bSelectedCustomerUnlimitedForeignTransferThreshold = false

    this.SelectedCustomerFDThreshold = 0
    this.SelectedCustomerInternalTransferThreshold = 0
    this.SelectedCustomerDhabiTransferThreshold = 0
    this.SelectedCustomerForeignTransferThreshold = 0

    this.bValidFDThreshold = this.bUndefined
    this.bValidInternalTxThreshold = this.bUndefined
    this.bValidDhabiTxThreshold = this.bUndefined
    this.bValidForeignTxThreshold = this.bUndefined

    this.resetCifThresholdState()
  }

  resetCommomPermissions() {
    this.bSelectedCustomerCreateSB = false
    this.bSelectedCustomerCreateCurrent = false
    this.bSelectedCustomerRequestCertificate = false
    this.bSelectedCustomerChangeMobile = false
    this.bSelectedCustomerChangeAddress = false
    this.bSelectedCustomerChangeEmail = false
    this.bSelectedCustomerFullMasterAuthority = false
  }

  // ===== Account-permissions grid =====
  // Build the account grid rows from company accounts and existing account-level matrices.
  // CIF-level entries are ignored by the grid and preserved as-is.
  buildAccountMatrixGrid() {
    const permissions = this.SelectedCustomerAccountPermissions || []
    const matchesView = (m: any) =>
      (this.bShowingApprovedData && m.isDecisionTaken) ||
      (!this.bShowingApprovedData && !m.isDecisionTaken)

    const buildCell = (matches: any[], matrixId: number) => {
      const m = matches.find((x: any) => x.matrixId === matrixId)
      if (!m) {
        return { enabled: false, threshold: 0, unlimited: false }
      }
      const isUnlimited = m.threshold === -1
      return {
        enabled: !!m.enable,
        threshold: isUnlimited ? 0 : (m.threshold || 0),
        unlimited: isUnlimited
      }
    }

    this.accountMatrixRows = (this.companyAccounts || []).map((accountNumber: string) => {
      const forAccount = permissions.filter((m: any) =>
        !m.isCIFLevelAuthority && m.accountNumber === accountNumber && matchesView(m)
      )
      const fd = buildCell(forAccount, 1)
      const internal = buildCell(forAccount, 14)
      const dhabi = buildCell(forAccount, 12)
      const foreign = buildCell(forAccount, 13)
      return {
        accountNumber,
        currency: (this.accountCurrencyByNumber[accountNumber] || 'USD').toUpperCase(),
        rowEnabled: fd.enabled || internal.enabled || dhabi.enabled || foreign.enabled,
        fd, internal, dhabi, foreign
      }
    })

    this.recalculateAccountColumnSelectAll()
  }

  private recalculateAccountColumnSelectAll() {
    const rows = this.accountMatrixRows || []
    const allEnabled = (getter: (r: any) => boolean) => rows.length > 0 && rows.every(getter)
    this.allAccountsSelected = allEnabled(r => r.rowEnabled)
    this.allFDSelected = allEnabled(r => r.fd.enabled)
    this.allInternalSelected = allEnabled(r => r.internal.enabled)
    this.allDhabiSelected = allEnabled(r => r.dhabi.enabled)
    this.allForeignSelected = allEnabled(r => r.foreign.enabled)
  }

  private persistAccountMatrixCell(matrixId: number, accountNumber: string, cell: { enabled: boolean, threshold: number, unlimited: boolean }) {
    const thresholdValue = cell.enabled ? (cell.unlimited ? -1 : (cell.threshold || 0)) : 0
    this.addOrUpdateAccountLevelMatrices(
      matrixId,
      accountNumber,
      this.searchCif,
      cell.enabled,
      thresholdValue,
      this.bIsSelectedCustomerMaker
    )
  }

  toggleAccountRow(row: any) {
    const newState = !row.rowEnabled
    row.rowEnabled = newState
    this.accountMatrixColumns.forEach(col => {
      const cell = row[col.key]
      cell.enabled = newState
      if (!newState) {
        cell.threshold = 0
        cell.unlimited = false
      }
      this.persistAccountMatrixCell(col.matrixId, row.accountNumber, cell)
    })
    this.recalculateAccountColumnSelectAll()
    this.bCustomerPermissionsEdited = true
  }

  toggleAllAccountsSelectAll() {
    const newState = !this.allAccountsSelected
    this.allAccountsSelected = newState
    ;(this.accountMatrixRows || []).forEach((row: any) => {
      row.rowEnabled = newState
      this.accountMatrixColumns.forEach(col => {
        const cell = row[col.key]
        cell.enabled = newState
        if (!newState) {
          cell.threshold = 0
          cell.unlimited = false
        }
        this.persistAccountMatrixCell(col.matrixId, row.accountNumber, cell)
      })
    })
    this.recalculateAccountColumnSelectAll()
    this.bCustomerPermissionsEdited = true
  }

  toggleAccountColumnSelectAll(cellKey: 'fd' | 'internal' | 'dhabi' | 'foreign', matrixId: number) {
    const flagKey: string = ({ fd: 'allFDSelected', internal: 'allInternalSelected', dhabi: 'allDhabiSelected', foreign: 'allForeignSelected' } as any)[cellKey]
    const newVal = !(this as any)[flagKey]
    ;(this as any)[flagKey] = newVal
    ;(this.accountMatrixRows || []).forEach((row: any) => {
      const cell = row[cellKey]
      cell.enabled = newVal
      if (!newVal) {
        cell.threshold = 0
        cell.unlimited = false
      }
      row.rowEnabled = row.fd.enabled || row.internal.enabled || row.dhabi.enabled || row.foreign.enabled
      this.persistAccountMatrixCell(matrixId, row.accountNumber, cell)
    })
    this.recalculateAccountColumnSelectAll()
    this.bCustomerPermissionsEdited = true
  }

  onAccountCellThresholdChange(event: any, row: any, cellKey: 'fd' | 'internal' | 'dhabi' | 'foreign', matrixId: number) {
    const cell = row[cellKey]
    const raw = (event?.target?.value ?? '').toString().replaceAll(',', '')
    const parsed = parseFloat(raw)
    cell.threshold = isNaN(parsed) ? 0 : parsed
    cell.unlimited = false
    if (!cell.enabled && cell.threshold > 0) {
      cell.enabled = true
      row.rowEnabled = true
    }
    this.persistAccountMatrixCell(matrixId, row.accountNumber, cell)
    this.recalculateAccountColumnSelectAll()
    this.bCustomerPermissionsEdited = true
  }

  onAccountCellUnlimitedToggle(row: any, cellKey: 'fd' | 'internal' | 'dhabi' | 'foreign', matrixId: number) {
    const cell = row[cellKey]
    cell.unlimited = !cell.unlimited
    if (cell.unlimited) {
      cell.threshold = 0
      cell.enabled = true
      row.rowEnabled = true
    }
    this.persistAccountMatrixCell(matrixId, row.accountNumber, cell)
    this.recalculateAccountColumnSelectAll()
    this.bCustomerPermissionsEdited = true
  }

  addOrUpdateAccountLevelMatrices(matrixId: number, accountNumber: string, cif: string, enable: boolean, threshold: number, isMaker: boolean) {
    let bFoundMatch = false

    this.SelectedCustomerAccountPermissions.forEach((element: any) => {
      if (element.matrixId == matrixId && element.accountNumber == accountNumber) {
        // Update old matrix
        bFoundMatch = true
        element.enable = enable
        if (!Number.isNaN(threshold))
          element.threshold = threshold
        console.log("Old Matrix Updated")
        console.log(element)
      }
    });

    if (!bFoundMatch) {
      // Add a New Matrix
      let matrixDetails = {
        matrixId: matrixId,
        accountNumber: accountNumber,
        cif: cif,
        enable: enable,
        threshold: threshold,
        disapprovalReason: null,
        isApproved: false,
        isCIFLevelAuthority: false,
        isDecisionTaken: false,
        isMaker: isMaker
      }

      this.SelectedCustomerAccountPermissions.push(matrixDetails)
      console.log("New Matrix Added")
      console.log(this.SelectedCustomerAccountPermissions)
    }
  }

  addOrUpdateCIFLevelMatrices(
    matrixId: number,
    accountNumber: string,
    cif: string,
    enable: boolean,
    threshold: number,
    isMaker: boolean) {
    let bFoundMatch = false

    this.selectedItem.item.approvalMatrices.forEach((element: any) => {
      if (element.matrixId == matrixId) {
        // Update old matrix
        bFoundMatch = true
        element.enable = enable
        if (!Number.isNaN(threshold))
          element.threshold = threshold
      }
    });

    if (!bFoundMatch) {
      // Add a New Matrix
      let matrixDetails = {
        matrixId: matrixId,
        accountNumber: '',
        cif: cif,
        enable: enable,
        threshold: threshold,
        disapprovalReason: null,
        isApproved: false,
        isCIFLevelAuthority: true,
        isDecisionTaken: false,
        isMaker: isMaker
      }

      this.selectedItem.item.approvalMatrices.push(matrixDetails)
    }

    console.log(this.selectedItem.item.approvalMatrices)
  }

  setAccountMatrices() {

    this.resetMatrixControls()

    this.SelectedCustomerAccountPermissions.forEach((element: any) => {

      if (element.accountNumber === this.SelectedCustomerAccount) {
        if ((this.bShowingApprovedData && element.isDecisionTaken) || (!this.bShowingApprovedData && !element.isDecisionTaken)) {

          switch (element.matrixId) {
            case 15:
              this.bSelectedCustomerViewOnly = element.enable
              break;
            case 16:
              this.bSelectedCustomerDeactivateAccount = element.enable
              break;
            case 1:
              this.bSelectedCustomerCanCreateFD = element.enable

              if (element.threshold == -1) {
                this.bSelectedCustomerUnlimitedFDThreshold = true
                this.SelectedCustomerFDThreshold = 0
              }
              else {
                this.bSelectedCustomerUnlimitedFDThreshold = false
                this.SelectedCustomerFDThreshold = element.threshold
              }

              break;
            case 14:
              this.bSelectedCustomerCanMakeInternalTransfer = element.enable

              if (element.threshold == -1) {
                this.bSelectedCustomerUnlimitedInternalTransferThreshold = true
                this.SelectedCustomerInternalTransferThreshold = 0
              }
              else {
                this.bSelectedCustomerUnlimitedInternalTransferThreshold = false
                this.SelectedCustomerInternalTransferThreshold = element.threshold
              }
              break;
            case 12:
              this.bSelectedCustomerCanMakeDhabiTransfer = element.enable

              if (element.threshold == -1) {
                this.bSelectedCustomerUnlimitedDhabiTransferThreshold = true
                this.SelectedCustomerDhabiTransferThreshold = 0
              }
              else {
                this.bSelectedCustomerUnlimitedDhabiTransferThreshold = false
                this.SelectedCustomerDhabiTransferThreshold = element.threshold
              }

              break;
            case 13:
              this.bSelectedCustomerCanMakeForeignTransfer = element.enable

              if (element.threshold == -1) {
                this.bSelectedCustomerUnlimitedForeignTransferThreshold = true
                this.SelectedCustomerForeignTransferThreshold = 0
              }
              else {
                this.bSelectedCustomerUnlimitedForeignTransferThreshold = false
                this.SelectedCustomerForeignTransferThreshold = element.threshold
              }

              break;
          }
        } else {
          switch (element.matrixId) {
            case 15:
              this.bSelectedCustomerViewOnly = true
              break;
            case 16:
              this.bSelectedCustomerDeactivateAccount = true
              break;
            case 1:
              this.bSelectedCustomerCanCreateFD = true

              if (element.threshold == -1) {
                this.bSelectedCustomerUnlimitedFDThreshold = true
                this.SelectedCustomerFDThreshold = 0
              }
              else {
                this.bSelectedCustomerUnlimitedFDThreshold = false
                this.SelectedCustomerFDThreshold = element.threshold
              }

              break;
            case 14:
              this.bSelectedCustomerCanMakeInternalTransfer = true

              if (element.threshold == -1) {
                this.bSelectedCustomerUnlimitedInternalTransferThreshold = true
                this.SelectedCustomerInternalTransferThreshold = 0
              }
              else {
                this.bSelectedCustomerUnlimitedInternalTransferThreshold = false
                this.SelectedCustomerInternalTransferThreshold = element.threshold
              }
              break;
            case 12:
              this.bSelectedCustomerCanMakeDhabiTransfer = true

              if (element.threshold == -1) {
                this.bSelectedCustomerUnlimitedDhabiTransferThreshold = true
                this.SelectedCustomerDhabiTransferThreshold = 0
              }
              else {
                this.bSelectedCustomerUnlimitedDhabiTransferThreshold = false
                this.SelectedCustomerDhabiTransferThreshold = element.threshold
              }

              break;
            case 13:
              this.bSelectedCustomerCanMakeForeignTransfer = true

              if (element.threshold == -1) {
                this.bSelectedCustomerUnlimitedForeignTransferThreshold = true
                this.SelectedCustomerForeignTransferThreshold = 0
              }
              else {
                this.bSelectedCustomerUnlimitedForeignTransferThreshold = false
                this.SelectedCustomerForeignTransferThreshold = element.threshold
              }

              break;
          }
        }

      }
    });
  }

  setMasterAuthority(matrixId: number) {
    console.log(matrixId, this.bIsSelectedCustomerMaker);
    this.bCustomerPermissionsEdited = true
    switch (matrixId) {
      case 0:
        console.log(this.bSelectedCustomerCreateSB, this.bSelectedCustomerCreateCurrent, this.bSelectedCustomerChangeAddress, this.bSelectedCustomerChangeMobile)
        this.bSelectedCustomerFullMasterAuthority = !this.bSelectedCustomerFullMasterAuthority

        this.bSelectedCustomerCreateSB = this.bSelectedCustomerFullMasterAuthority
        this.bSelectedCustomerCreateCurrent = this.bSelectedCustomerFullMasterAuthority
        this.bSelectedCustomerChangeAddress = this.bSelectedCustomerFullMasterAuthority
        this.bSelectedCustomerChangeMobile = this.bSelectedCustomerFullMasterAuthority
        this.bSelectedCustomerChangeEmail = this.bSelectedCustomerFullMasterAuthority
        this.bSelectedCustomerRequestCertificate = this.bSelectedCustomerFullMasterAuthority

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.addOrUpdateAccountLevelMatrices(
            2,
            '',
            this.searchCif,
            this.bSelectedCustomerCreateSB,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.addOrUpdateAccountLevelMatrices(
            3,
            '',
            this.searchCif,
            this.bSelectedCustomerCreateCurrent,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.addOrUpdateAccountLevelMatrices(
            4,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeAddress,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.addOrUpdateAccountLevelMatrices(
            5,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeMobile,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.addOrUpdateAccountLevelMatrices(
            6,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeEmail,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.addOrUpdateAccountLevelMatrices(
            9,
            '',
            this.searchCif,
            this.bSelectedCustomerRequestCertificate,
            0,
            this.bIsSelectedCustomerMaker
          )
        } else {

          this.addOrUpdateCIFLevelMatrices(
            2,
            '',
            this.searchCif,
            this.bSelectedCustomerCreateSB,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.addOrUpdateCIFLevelMatrices(
            3,
            '',
            this.searchCif,
            this.bSelectedCustomerCreateCurrent,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.addOrUpdateCIFLevelMatrices(
            4,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeAddress,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.addOrUpdateCIFLevelMatrices(
            5,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeMobile,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.addOrUpdateCIFLevelMatrices(
            6,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeEmail,
            0,
            this.bIsSelectedCustomerMaker
          )

          this.addOrUpdateCIFLevelMatrices(
            9,
            '',
            this.searchCif,
            this.bSelectedCustomerRequestCertificate,
            0,
            this.bIsSelectedCustomerMaker
          )
        }
        break;
      case 2:
        this.bSelectedCustomerCreateSB = !this.bSelectedCustomerCreateSB

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.addOrUpdateAccountLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerCreateSB,
            0,
            this.bIsSelectedCustomerMaker
          )
        } else {
          this.addOrUpdateCIFLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerCreateSB,
            0,
            this.bIsSelectedCustomerMaker
          )
        }
        break;
      case 3:
        this.bSelectedCustomerCreateCurrent = !this.bSelectedCustomerCreateCurrent

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.addOrUpdateAccountLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerCreateCurrent,
            0,
            this.bIsSelectedCustomerMaker
          )
        } else {
          this.addOrUpdateCIFLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerCreateCurrent,
            0,
            this.bIsSelectedCustomerMaker
          )
        }
        break;
      case 4:
        this.bSelectedCustomerChangeAddress = !this.bSelectedCustomerChangeAddress

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.addOrUpdateAccountLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeAddress,
            0,
            this.bIsSelectedCustomerMaker
          )
        } else {
          this.addOrUpdateCIFLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeAddress,
            0,
            this.bIsSelectedCustomerMaker
          )
        }
        break;
      case 5:
        this.bSelectedCustomerChangeMobile = !this.bSelectedCustomerChangeMobile

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.addOrUpdateAccountLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeMobile,
            0,
            this.bIsSelectedCustomerMaker
          )
        } else {
          this.addOrUpdateCIFLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeMobile,
            0,
            this.bIsSelectedCustomerMaker
          )
        }
        break;
      case 6:
        this.bSelectedCustomerChangeEmail = !this.bSelectedCustomerChangeEmail

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.addOrUpdateAccountLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeEmail,
            0,
            this.bIsSelectedCustomerMaker
          )
        } else {
          this.addOrUpdateCIFLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerChangeEmail,
            0,
            this.bIsSelectedCustomerMaker
          )
        }
        break;
      case 9:
        this.bSelectedCustomerRequestCertificate = !this.bSelectedCustomerRequestCertificate

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.addOrUpdateAccountLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerRequestCertificate,
            0,
            this.bIsSelectedCustomerMaker
          )
        } else {
          this.addOrUpdateCIFLevelMatrices(
            matrixId,
            '',
            this.searchCif,
            this.bSelectedCustomerRequestCertificate,
            0,
            this.bIsSelectedCustomerMaker
          )
        }
        break;
    }

    if (this.bSelectedCustomerChangeAddress &&
      this.bSelectedCustomerChangeEmail &&
      this.bSelectedCustomerChangeMobile &&
      this.bSelectedCustomerCreateCurrent &&
      this.bSelectedCustomerCreateSB &&
      this.bSelectedCustomerRequestCertificate)
      this.bSelectedCustomerFullMasterAuthority = true
    else
      this.bSelectedCustomerFullMasterAuthority = false
  }

  getSelectedGender() {
    switch (this.selectedItem.item.genderId) {
      case 1:
        return 'Male'
      case 2:
        return 'Female'
      default:
        return 'Male'
    }
  }

  getSelectedIDType() {
    switch (this.selectedItem.item.isEID) {
      case true:
        return 'Emirates ID'
      case false:
        return 'Passport'
      default:
        return 'Unknown'
    }
  }

  goBackToSearchScreen() {
    //console.log(this.bNewCustomer)
    if (this.bCustomerProfileEdited || this.bCustomerPermissionsEdited) {
      this.showAlertBox('Are you sure?', 'You have modified the customer. Do you want to just ignore changes and go back to the earlier screen?',
        "No, I don't want to go back", true, "Yes, I am sure", { action: 2, matrixId: 0 })
    } else {
      this.bShowCifSearchScreen = true
      this.bShowCustomerDetailsScreen = false
      this.resetCommomPermissions()
      this.resetMatrixControls()
      this.SelectedCustomerAccountPermissions = []
      this.selectedItem = []

      if (this.selectedSearchButton == 1)
        this.getCifDetails()
      else if (this.selectedSearchButton == 2)
        this.getRejectedCustomers()
      else if (this.selectedSearchButton == 3)
        this.getAllRejectedCustomers()
      else if (this.selectedSearchButton == 4)
        this.getCifDetails()
    }
  }

  getStatusBadge(item: any) {
    if (item.isActiveDeactiveRequestPending) {
      return 'danger'
    } else {
      if ((item.isDecisionTaken && item.approvalMatrices.length != 0 && item.approvalMatrices[0].isDecisionTaken) && !item.isActive) {
        return 'danger'
      } else {
        if (item.approvalMatrices != undefined && item.approvalMatrices[0] != undefined) {
          if (item.isDecisionTaken && item.isApproved && item.approvalMatrices[0].isDecisionTaken && item.approvalMatrices[0].isApproved)
            return 'success'
          else if ((item.isDecisionTaken && !item.isApproved) || (item.approvalMatrices[0].isDecisionTaken && !item.approvalMatrices[0].isApproved))
            return 'danger'
          else if (!item.isDecisionTaken || !item.approvalMatrices[0].isDecisionTaken)
            return 'warning'
          else
            return 'primary'
        } else {
          return 'danger'
        }
      }
    }
  }

  getRoleBadge(item: any) {
    if (item.approvalMatrices != undefined && item.approvalMatrices[0] != undefined) {
      if (item.approvalMatrices[0].isMaker)
        return 'primary'
      else
        return 'secondary'
    } else {
      return 'danger'
    }
  }

  getAuthorityLevelBadge(item: any) {
    if (item.approvalMatrices != undefined && item.approvalMatrices[0] != undefined) {
      if (item.approvalMatrices[0].isCIFLevelAuthority)
        return 'primary'
      else
        return 'secondary'
    } else {
      return 'danger'
    }
  }

  getRole(item: any) {
    if (item.approvalMatrices != undefined && item.approvalMatrices[0] != undefined) {
      if (item.approvalMatrices[0].isMaker)
        return 'Maker'
      else
        return 'Checker'
    } else {
      return 'Unknown'
    }
  }

  getAuthorityLevel(item: any) {
    if (item.approvalMatrices != undefined && item.approvalMatrices[0] != undefined) {
      let authorityLevel = ''

      item.approvalMatrices.every((element: any) => {
        // First see if there 'enable' requests
        if (element.enable && element.isCIFLevelAuthority) {
          authorityLevel = 'CIF'
          return false
        }
        else if (element.enable && !element.isCIFLevelAuthority) {
          authorityLevel = 'Account'
          return false
        }

        if (element.isCIFLevelAuthority) {
          authorityLevel = 'CIF'
        }
        else if (!element.isCIFLevelAuthority) {
          authorityLevel = 'Account'
        }

        return true
      })

      return authorityLevel
    } else {
      return 'Unknown'
    }
  }

  getStatus(item: any) {
    if (item.isActiveDeactiveRequestPending) {
      return 'Active/Inactive Decision Pending'
    } else {
      if (item.isDeactivated) {
        return 'Inactive Customer'
      } else {
        // if(item.isDecisionTaken && item.isApproved)
        // return 'Approved'
        // else if(item.isDecisionTaken && !item.isApproved)
        //   return 'Rejected'
        // else if(!item.isDecisionTaken)
        //   return 'No Decision Taken'
        // else if(!)
        //   return 'No Decision Taken'
        // else{
        //   return 'Incomplete'
        // }


        if (item.approvalMatrices != undefined && item.approvalMatrices[0] != undefined) {
          if (item.isDecisionTaken && item.isApproved && item.approvalMatrices[0].isDecisionTaken && item.approvalMatrices[0].isApproved)
            return 'Approved'
          else if ((item.isDecisionTaken && !item.isApproved) || (item.approvalMatrices[0].isDecisionTaken && !item.approvalMatrices[0].isApproved))
            return 'Rejected'
          else if (!item.isDecisionTaken || !item.approvalMatrices[0].isDecisionTaken)
            return 'No Decision Taken'
          else
            return 'Unknown'
        } else {
          return 'Incomplete'
        }
      }
    }
  }

  enableEditing() {
    this.bViewOnly = true
    this.customerProfileForm.enable()
  }

  formatNumber(event: any, matrixId: number) {
    this.bCustomerPermissionsEdited = true
    var cleanValue = event.srcElement.value.replaceAll(',', '')
    var tempValue = parseFloat(cleanValue)
    var bValidNumber = false

    if (cleanValue.length !== tempValue.toString().length)
      bValidNumber = false
    else
      bValidNumber = true

    //if (!Number.isNaN(tempValue))
    {
      switch (matrixId) {
        case 1:
          if (!this.bSelectedCustomerUnlimitedFDThreshold && tempValue <= 0)
            bValidNumber = false

          this.SelectedCustomerFDThreshold = tempValue
          this.bValidFDThreshold = bValidNumber

          if (bValidNumber && !this.bSelectedCustomerCIFLevelAuthority) {
            this.addOrUpdateAccountLevelMatrices(
              1,
              this.SelectedCustomerAccount,
              this.searchCif,
              this.bSelectedCustomerCanCreateFD,
              this.SelectedCustomerFDThreshold,
              this.bIsSelectedCustomerMaker
            )
          } else if (bValidNumber && this.bSelectedCustomerCIFLevelAuthority) {
            this.addOrUpdateCIFLevelMatrices(
              1,
              '',
              this.searchCif,
              this.bSelectedCustomerCanCreateFD,
              this.SelectedCustomerFDThreshold,
              this.bIsSelectedCustomerMaker
            )
          }
          break;
        case 12:
          if (!this.bSelectedCustomerUnlimitedDhabiTransferThreshold && tempValue <= 0)
            bValidNumber = false

          this.SelectedCustomerDhabiTransferThreshold = tempValue
          this.bValidDhabiTxThreshold = bValidNumber

          if (!this.bSelectedCustomerCIFLevelAuthority) {
            this.addOrUpdateAccountLevelMatrices(
              12,
              this.SelectedCustomerAccount,
              this.searchCif,
              this.bSelectedCustomerCanMakeDhabiTransfer,
              this.SelectedCustomerDhabiTransferThreshold,
              this.bIsSelectedCustomerMaker
            )
          } else if (bValidNumber && this.bSelectedCustomerCIFLevelAuthority) {
            this.addOrUpdateCIFLevelMatrices(
              12,
              '',
              this.searchCif,
              this.bSelectedCustomerCanMakeDhabiTransfer,
              this.SelectedCustomerDhabiTransferThreshold,
              this.bIsSelectedCustomerMaker
            )
          }
          break;
        case 13:
          if (!this.bSelectedCustomerUnlimitedForeignTransferThreshold && tempValue <= 0)
            bValidNumber = false

          this.SelectedCustomerForeignTransferThreshold = tempValue
          this.bValidForeignTxThreshold = bValidNumber

          if (!this.bSelectedCustomerCIFLevelAuthority) {
            this.addOrUpdateAccountLevelMatrices(
              13,
              this.SelectedCustomerAccount,
              this.searchCif,
              this.bSelectedCustomerCanMakeForeignTransfer,
              this.SelectedCustomerForeignTransferThreshold,
              this.bIsSelectedCustomerMaker
            )
          } else if (bValidNumber && this.bSelectedCustomerCIFLevelAuthority) {
            this.addOrUpdateCIFLevelMatrices(
              13,
              '',
              this.searchCif,
              this.bSelectedCustomerCanMakeForeignTransfer,
              this.SelectedCustomerForeignTransferThreshold,
              this.bIsSelectedCustomerMaker
            )
          }
          break;
        case 14:
          if (!this.bSelectedCustomerUnlimitedInternalTransferThreshold && tempValue <= 0)
            bValidNumber = false

          this.SelectedCustomerInternalTransferThreshold = tempValue
          this.bValidInternalTxThreshold = bValidNumber

          if (!this.bSelectedCustomerCIFLevelAuthority) {
            this.addOrUpdateAccountLevelMatrices(
              14,
              this.SelectedCustomerAccount,
              this.searchCif,
              this.bSelectedCustomerCanMakeInternalTransfer,
              this.SelectedCustomerInternalTransferThreshold,
              this.bIsSelectedCustomerMaker
            )
          } else if (bValidNumber && this.bSelectedCustomerCIFLevelAuthority) {
            this.addOrUpdateCIFLevelMatrices(
              14,
              '',
              this.searchCif,
              this.bSelectedCustomerCanMakeInternalTransfer,
              this.SelectedCustomerInternalTransferThreshold,
              this.bIsSelectedCustomerMaker
            )
          }
          break;
      }

    }
  }

  setCIFAuthority(matrixId: number, event: any = null) {
    this.bCustomerPermissionsEdited = true
    switch (matrixId) {
      case 1:
        this.bSelectedCustomerCanCreateFD = !this.bSelectedCustomerCanCreateFD

        if (!this.bSelectedCustomerCanCreateFD) {
          this.SelectedCustomerFDThreshold = 0
          this.bSelectedCustomerUnlimitedFDThreshold = false
          this.bValidFDThreshold = this.bUndefined
        }

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.setAccountPermission(1)
        } else {
          this.setCIFPermission(1)
        }
        break;
      case 12:
        this.bSelectedCustomerCanMakeDhabiTransfer = !this.bSelectedCustomerCanMakeDhabiTransfer

        if (!this.bSelectedCustomerCanMakeDhabiTransfer) {
          this.SelectedCustomerDhabiTransferThreshold = 0
          this.bSelectedCustomerUnlimitedDhabiTransferThreshold = false
          this.bValidDhabiTxThreshold = this.bUndefined
        }

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.setAccountPermission(12)
        } else {
          this.setCIFPermission(12)
        }
        break;
      case 13:
        this.bSelectedCustomerCanMakeForeignTransfer = !this.bSelectedCustomerCanMakeForeignTransfer

        if (!this.bSelectedCustomerCanMakeForeignTransfer) {
          this.SelectedCustomerForeignTransferThreshold = 0
          this.bSelectedCustomerUnlimitedForeignTransferThreshold = false
          this.bValidForeignTxThreshold = this.bUndefined
        }
        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.setAccountPermission(13)
        } else {
          this.setCIFPermission(13)
        }
        break;
      case 14:
        this.bSelectedCustomerCanMakeInternalTransfer = !this.bSelectedCustomerCanMakeInternalTransfer

        if (!this.bSelectedCustomerCanMakeInternalTransfer) {
          this.SelectedCustomerInternalTransferThreshold = 0
          this.bSelectedCustomerUnlimitedInternalTransferThreshold = false
          this.bValidInternalTxThreshold = this.bUndefined
        }
        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.setAccountPermission(14)
        } else {
          this.setCIFPermission(14)
        }
        break;
      case 15:
        if (!this.bSelectedCustomerViewOnly) {
          //event.stopPropagation()
          event.preventDefault()

          this.showAlertBox(
            'Warning',
            'All other permissions will be removed. Are you sure you want to do this?',
            'No', true, 'Yes',
            { action: 1, matrixId: 15 })
          if (!this.bSelectedCustomerCIFLevelAuthority)
            this.setAccountPermission(15)
          else
            this.setCIFPermission(15)
          console.log(this.finalMatrices)

        } else {
          this.bSelectedCustomerViewOnly = !this.bSelectedCustomerViewOnly
          if (!this.bSelectedCustomerCIFLevelAuthority)
            this.setAccountPermission(15)
          else
            this.setCIFPermission(15)
        }
        break;
      case 16:
        this.bSelectedCustomerDeactivateAccount = !this.bSelectedCustomerViewOnly
        console.log(this.bSelectedCustomerDeactivateAccount)
        //----> check log for trueconsole.log(this.bSelectedCustomerCIFLevelAuthority)
        if (!this.bSelectedCustomerCIFLevelAuthority) {
          console.log(event.target.checked)
          this.bSelectedCustomerDeactivateAccount = event.target.checked;
          this.setAccountPermission(16)
        } else {
          this.setCIFPermission(16)
        }
        break;
    }
  }

  setUnlimitedSwitches(matrixId: number) {
    switch (matrixId) {
      case 1:
        this.bSelectedCustomerUnlimitedFDThreshold = !this.bSelectedCustomerUnlimitedFDThreshold

        if (this.bSelectedCustomerUnlimitedFDThreshold) {
          this.SelectedCustomerFDThreshold = -1
          this.bValidFDThreshold = this.bUndefined
        }
        else {
          this.SelectedCustomerFDThreshold = 0
          this.bValidFDThreshold = false
        }

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.setAccountPermission(1)
        } else {
          this.setCIFPermission(1)
        }
        break;
      case 12:
        this.bSelectedCustomerUnlimitedDhabiTransferThreshold = !this.bSelectedCustomerUnlimitedDhabiTransferThreshold

        if (this.bSelectedCustomerUnlimitedDhabiTransferThreshold) {
          this.SelectedCustomerDhabiTransferThreshold = -1
          this.bValidDhabiTxThreshold = this.bUndefined
        }
        else {
          this.SelectedCustomerDhabiTransferThreshold = 0
          this.bValidDhabiTxThreshold = false
        }

        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.setAccountPermission(12)
        } else {
          this.setCIFPermission(12)
        }
        break;
      case 13:
        this.bSelectedCustomerUnlimitedForeignTransferThreshold = !this.bSelectedCustomerUnlimitedForeignTransferThreshold

        if (this.bSelectedCustomerUnlimitedForeignTransferThreshold) {
          this.SelectedCustomerForeignTransferThreshold = -1
          this.bValidForeignTxThreshold = this.bUndefined
        }
        else {
          this.SelectedCustomerForeignTransferThreshold = 0
          this.bValidForeignTxThreshold = false
        }


        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.setAccountPermission(13)
        } else {
          this.setCIFPermission(13)
        }
        break;
      case 14:
        this.bSelectedCustomerUnlimitedInternalTransferThreshold = !this.bSelectedCustomerUnlimitedInternalTransferThreshold

        if (this.bSelectedCustomerUnlimitedInternalTransferThreshold) {
          this.SelectedCustomerInternalTransferThreshold = -1
          this.bValidInternalTxThreshold = this.bUndefined
        }
        else {
          this.SelectedCustomerInternalTransferThreshold = 0
          this.bValidInternalTxThreshold = false
        }


        if (!this.bSelectedCustomerCIFLevelAuthority) {
          this.setAccountPermission(14)
        } else {
          this.setCIFPermission(14)
        }
        break;
    }
  }

  setAccountPermission(matrixId: number) {
    console.log(matrixId, "Ac Permission", this.bSelectedCustomerDeactivateAccount)
    let value = 0
    let enable: any = false

    switch (matrixId) {
      case 1:
        value = this.SelectedCustomerFDThreshold
        enable = this.bSelectedCustomerCanCreateFD
        break;
      case 12:
        value = this.SelectedCustomerDhabiTransferThreshold
        enable = this.bSelectedCustomerCanMakeDhabiTransfer
        break;
      case 13:
        value = this.SelectedCustomerForeignTransferThreshold
        enable = this.bSelectedCustomerCanMakeForeignTransfer
        break
      case 14:
        value = this.SelectedCustomerInternalTransferThreshold
        enable = this.bSelectedCustomerCanMakeInternalTransfer
        break
      case 15:
        value = 0
        enable = this.bSelectedCustomerViewOnly
        break
      case 16:
        value = 0
        enable = this.bSelectedCustomerDeactivateAccount
        break
    }

    this.addOrUpdateAccountLevelMatrices(
      matrixId,
      this.SelectedCustomerAccount,
      this.searchCif,
      enable,
      value,
      this.bIsSelectedCustomerMaker
    )
  }

  setCIFPermission(matrixId: number) {
    let value = 0
    let enable = false

    switch (matrixId) {
      case 1:
        value = this.SelectedCustomerFDThreshold
        enable = this.bSelectedCustomerCanCreateFD
        break;
      case 12:
        value = this.SelectedCustomerDhabiTransferThreshold
        enable = this.bSelectedCustomerCanMakeDhabiTransfer
        break;
      case 13:
        value = this.SelectedCustomerForeignTransferThreshold
        enable = this.bSelectedCustomerCanMakeForeignTransfer
        break
      case 14:
        value = this.SelectedCustomerInternalTransferThreshold
        enable = this.bSelectedCustomerCanMakeInternalTransfer
        break
      case 15:
        value = 0
        enable = this.bSelectedCustomerViewOnly
        break
      case 16:
        value = 0
        enable = this.bSelectedCustomerDeactivateAccount
        break
    }

    this.addOrUpdateCIFLevelMatrices(
      matrixId,
      '',
      this.searchCif,
      enable,
      value,
      this.bIsSelectedCustomerMaker
    )
  }

  proceed() {
    this.selectedTab = 1
    this.profileTabActive = false
    this.matrixTabActive = true
  }

  activeTabPaneIdxChange(event: any) {
    if (event == 0) {
      this.selectedTab = 0
      this.profileTabActive = true
      this.matrixTabActive = false
    }
    else if (event == 1) {
      this.selectedTab = 1
      this.profileTabActive = false
      this.matrixTabActive = true
    }
  }

  makeCIForAccountPermissionsReadonly() {
    this.SelectedCustomerDhabiTransferThreshold = 0
    this.SelectedCustomerFDThreshold = 0
    this.SelectedCustomerForeignTransferThreshold = 0
    this.SelectedCustomerInternalTransferThreshold = 0

    this.bSelectedCustomerCanCreateFD = false
    this.bSelectedCustomerCanMakeDhabiTransfer = false
    this.bSelectedCustomerCanMakeForeignTransfer = false
    this.bSelectedCustomerCanMakeInternalTransfer = false

    this.bSelectedCustomerUnlimitedDhabiTransferThreshold = false
    this.bSelectedCustomerUnlimitedFDThreshold = false
    this.bSelectedCustomerUnlimitedForeignTransferThreshold = false
    this.bSelectedCustomerUnlimitedInternalTransferThreshold = false

    this.setAccountPermission(1)
    this.setAccountPermission(12)
    this.setAccountPermission(13)
    this.setAccountPermission(14)
  }

  reloadCustomer() {

    //console.log(this.originalCustomerDetails.item)
    this.resetCommomPermissions()
    this.resetMatrixControls()

    // Copy back
    this.selectedItem = _.cloneDeep(this.originalCustomerDetails)

    this.selectedCustomerDateOfBirth = new Date(this.selectedItem.item.dateOfBirth)
    this.selectedCustomerIDExpiresOn = new Date(this.selectedItem.item.expiresOn)
    this.selectedCustomerGender = this.getSelectedGender()
    this.selectedCustomerIDType = this.getSelectedIDType()
    this.selectedCustomerRole = this.getSelectedRole()
    this.selectedCustomerAuthorityLevel = this.getSelectedAuthorityLevel()
    this.selectedCustomerNationality = this.resolveNationalityShortCode(this.selectedItem.item.nationalityCountryCode)

    this.customerName?.setValue(this.selectedItem.item.name)
    this.customerEmail?.setValue(this.selectedItem.item.emailId)
    this.customerMobile?.setValue(this.selectedItem.item.mobileNumber)
    this.customeGender?.setValue(this.selectedCustomerGender)
    this.customeDateOfBirth?.setValue(this.selectedCustomerDateOfBirth)
    this.customerIDType?.setValue(this.selectedCustomerIDType)
    this.customerIDNumber?.setValue(this.selectedItem.item.eidNumber)
    this.customerNationality?.setValue(this.resolveNationalityShortCode(this.selectedCustomerNationality))
    this.customerIDExpiryDate?.setValue(this.selectedCustomerIDExpiresOn)

    this.bShowCifSearchScreen = false
    this.bShowCustomerDetailsScreen = true
  }

  matrixChange(event: any) {

    this.bCustomerPermissionsEdited = true
  }

  saveCustomerPrompt() {
    let bError: boolean = false

    // Validate everything
    if (!this.customerProfileForm.valid) {
      this.showAlertBox('Sorry', 'Please ensure that you have entered everything correctly. Look for errors in the customer profile and correct them before you can save.')
      this.customerProfileForm.markAllAsTouched()
      return
    }
    //let regIdType = new RegExp('^(?=.*[A-Z])(?=.*[0-9])[A-Z0-9]+$')
    let regIdType = new RegExp('^[A-Za-z0-9]*$')
    if (!regIdType.test(this.customerIDNumber?.value) && this.customerIDType?.value === "Passport" && this.customerIDNumber?.touched) {
      this.showAlertBox('Sorry', 'ID Number accept alphabet and Numbers')
      return
    }
    // Save the personal profile to the selectedItem
    let dobTemp = this.customeDateOfBirth?.value
    let expTemp = this.customerIDExpiryDate?.value

    //console.log(dobTemp)

    this.selectedItem.item.name = this.customerName?.value
    this.selectedItem.item.dateOfBirth = dobTemp.getFullYear() + '-' + ('0' + (dobTemp.getMonth() + 1)).slice(-2) + '-' + ('0' + dobTemp.getDate()).slice(-2)
    this.selectedItem.item.eidNumber = this.customerIDNumber?.value
    this.selectedItem.item.emailId = this.customerEmail?.value
    this.selectedItem.item.expiresOn = expTemp.getFullYear() + '-' + ('0' + (expTemp.getMonth() + 1)).slice(-2) + '-' + ('0' + expTemp.getDate()).slice(-2)
    this.selectedItem.item.genderId = this.customeGender?.value == 'Male' ? 1 : 2
    this.selectedItem.item.mobileNumber = this.customerMobile?.value
    this.selectedItem.item.nationalityCountryCode = this.customerNationality?.value
    this.selectedItem.item.isEID = this.customerIDType?.value == 'Passport' ? false : true

    // If CIF level authority, check for selected matrices
    if (this.bSelectedCustomerCIFLevelAuthority) {
      if (this.bSelectedCustomerCanCreateFD && this.SelectedCustomerFDThreshold <= 0 && !this.bSelectedCustomerUnlimitedFDThreshold) {
        this.showAlertBox('Sorry', 'Please ensure that you have entered the correct FD threshold value. Correct the same before you can save.')
        bError = true
        this.bValidFDThreshold = false
        return
      }

      if (this.bSelectedCustomerCanMakeDhabiTransfer && this.SelectedCustomerDhabiTransferThreshold <= 0 && !this.bSelectedCustomerUnlimitedDhabiTransferThreshold) {
        this.showAlertBox('Sorry', 'Please ensure that you have entered the correct Dhabi Transfer threshold value. Correct the same before you can save.')
        bError = true
        this.bValidDhabiTxThreshold = false
        return
      }

      if (this.bSelectedCustomerCanMakeInternalTransfer && this.SelectedCustomerInternalTransferThreshold <= 0 && !this.bSelectedCustomerUnlimitedInternalTransferThreshold) {
        this.showAlertBox('Sorry', 'Please ensure that you have entered the correct Internal Transfer threshold value. Correct the same before you can save.')
        bError = true
        this.bValidInternalTxThreshold = false
        return
      }

      if (this.bSelectedCustomerCanMakeForeignTransfer && this.SelectedCustomerForeignTransferThreshold <= 0 && !this.bSelectedCustomerUnlimitedForeignTransferThreshold) {
        this.showAlertBox('Sorry', 'Please ensure that you have entered the correct Foreign Transfer threshold value. Correct the same before you can save.')
        bError = true
        this.bValidForeignTxThreshold = false
        return
      }
    }

    // If Account level authority, check for all the account related details
    if (!this.bSelectedCustomerCIFLevelAuthority) {
      this.SelectedCustomerAccountPermissions.forEach((element: any) => {
        // CIF-level rows are preserved as-is; skip them in account-level validation
        if (element.isCIFLevelAuthority) {
          return
        }
        let bUnlimited: boolean = element.threshold === -1 ? true : false

        switch (element.matrixId) {
          case 1:
            if (element.enable && element.threshold <= 0 && !bUnlimited) {
              console.log('Showing')
              this.showAlertBox('Sorry', 'Please ensure that you have entered the correct FD threshold value for the account ' + element.accountNumber + '. Correct the same before you can save.')
              bError = true
              return
            }
            break
          case 12:
            if (element.enable && element.threshold <= 0 && !bUnlimited) {
              console.log('Showing')
              this.showAlertBox('Sorry', 'Please ensure that you have entered the correct Dhabi Transfer threshold value for the account ' + element.accountNumber + '. Correct the same before you can save.')
              bError = true
              return
            }
            break

          case 13:
            if (element.enable && element.threshold <= 0 && !bUnlimited) {
              console.log('Showing')
              this.showAlertBox('Sorry', 'Please ensure that you have entered the correct Foreign Transfer threshold value for the account ' + element.accountNumber + '. Correct the same before you can save.')
              bError = true
              return
            }
            break

          case 14:
            if (element.enable && element.threshold <= 0 && !bUnlimited) {
              console.log('Showing')
              this.showAlertBox('Sorry', 'Please ensure that you have entered the correct Internal Transfer threshold value for the account ' + element.accountNumber + '. Correct the same before you can save.')
              bError = true
              return
            }
            break

        }
      })
    }

    if (!bError) {
      if (!this.bSelectedCustomerCIFLevelAuthority) {
        // Ensure account-level matrices have accountNumber/cif set; never overwrite CIF-level entries
        this.SelectedCustomerAccountPermissions.forEach((matrix: any) => {
          if (matrix.isCIFLevelAuthority) {
            return
          }
          if (!matrix.accountNumber || matrix.accountNumber === '') {
            matrix.accountNumber = this.SelectedCustomerAccount
          }
          if (!matrix.cif || matrix.cif === '') {
            matrix.cif = this.searchCif
          }
        })
        this.selectedItem.item.approvalMatrices = this.SelectedCustomerAccountPermissions
      } else {

      }

      if (!this.isCustomerRejected) {

      }
      this.finalMatrices = this.selectedItem.item.approvalMatrices
      console.log(this.finalMatrices)
      // if( !this.selectedItem.item.approvalMatrices.length || !this.SelectedCustomerAccountPermissions.length){
      //   this.showAlertBox("Warning", "This customer does not have any authority matrix defined")
      //   bError = true
      //   return
      // }
      /*console.log(this.selectedItem.item)
      if( !this.selectedItem.item.approvalMatrices.length || !this.SelectedCustomerAccountPermissions.length){
        this.showAlertBox("Warning", "This customer does not have any authority matrix defined")
        bError = true
        return
      }
      this.removeDuplicateMatrices()*/
      //this.removeDuplicateMatrices()

      if (this.bNewCustomer && this.finalMatrices.length == 0) // No changes to the proile and no new matrices
      {
        this.showAlertBox('Sorry', 'You need to add at least one permission for this customer before you can save')
        return
      } else

        this.showAlertBox('Confirm', 'Are you sure you want to save changes? If you say Yes, the Checker will be notified for approval.',
          'No', true, 'Yes', { action: 5, matrixId: 0 })
    }
  }

  showSaveButton() {
    if (this.bNewCustomer)
      return true

    if ((this.bCustomerProfileEdited || this.bCustomerPermissionsEdited))
      return true

    return false
  }
  hasProfileDataChanged() {
    if (this.bNewCustomer)
      return true

    // Print the customer


    if (this.selectedItem.item.name !== this.originalCustomerDetails.item.name)
      return true

    if (this.selectedItem.item.emailId !== this.originalCustomerDetails.item.emailId)
      return true

    if (this.selectedItem.item.mobileNumber !== this.originalCustomerDetails.item.mobileNumber)
      return true

    if (this.selectedItem.item.genderId !== this.originalCustomerDetails.item.genderId)
      return true

    if (this.selectedItem.item.dateOfBirth !== this.originalCustomerDetails.item.dateOfBirth)
      return true

    if (this.selectedItem.item.isEID !== this.originalCustomerDetails.item.isEID)
      return true

    if (this.selectedItem.item.eidNumber !== this.originalCustomerDetails.item.eidNumber)
      return true

    if (this.selectedItem.item.nationalityCountryCode !== this.originalCustomerDetails.item.nationalityCountryCode)
      return true

    if (this.selectedItem.item.expiresOn !== this.originalCustomerDetails.item.expiresOn)
      return true

    return false
  }

  finalMatrices: any = []

  removeDuplicateMatrices() {
    if (this.bNewCustomer)
      return

    this.finalMatrices = []
    console.log("CIF level authority: " + this.bSelectedCustomerCIFLevelAuthority)

    // If CIF level authority, check for selected matrices
    if (this.bSelectedCustomerCIFLevelAuthority) {
      this.selectedItem.item.approvalMatrices.forEach((selectedMatrix: any) => {
        let temp = this.originalCustomerDetails.item.approvalMatrices.find((x: {
          enable: any; matrixId: any; accountNumber: any; isMaker: any; threshold: any
        }) => {
          if (x.matrixId == selectedMatrix.matrixId &&
            x.enable == selectedMatrix.enable &&
            x.accountNumber == selectedMatrix.accountNumber &&
            x.isMaker == selectedMatrix.isMaker &&
            x.threshold == selectedMatrix.threshold)
            return true
          else
            return false
        })

        if (temp == undefined) {
          console.log('Pushed1')
          this.finalMatrices.push(selectedMatrix)
        }
      })
    } else {
      this.SelectedCustomerAccountPermissions.forEach((selectedMatrix: any) => {
        let temp = this.originalCustomerDetails.item.approvalMatrices.find((x: {
          enable: any; matrixId: any; accountNumber: any; isMaker: any; threshold: any
        }) => {
          if (x.matrixId == selectedMatrix.matrixId &&
            x.enable == selectedMatrix.enable &&
            x.accountNumber == selectedMatrix.accountNumber &&
            x.isMaker == selectedMatrix.isMaker &&
            x.threshold == selectedMatrix.threshold)
            return true
          else
            return false
        })

        if (temp == undefined) {
          console.log('Pushed2')
          this.finalMatrices.push(selectedMatrix)
        }
      })
    }

    console.log("111finalMatrix", this.finalMatrices)
  }

  processRoleOrAuthorityChange() {
    if (this.bCustomerRoleOrAuthorityChanged) {
      this.originalCustomerDetails.item.approvalMatrices = [];
      this.removeDuplicateMatrices();
      this.originalCustomerDetails.item.approvalMatrices.forEach((element: any) => {
        if (element.enable) {
          // Disable all active matrices
          element.enable = false
          this.finalMatrices.push(element)

        }
      })
    }
  }

  saveCustomer() {
    if (this.bNewCustomer)
      return this.saveNewCustomer()

    // Process role or authority changes, if any
    this.processRoleOrAuthorityChange()
    console.log("Final Matrix")
    console.log("save", this.finalMatrices)

    //this.bCustomerProfileEdited = false
    //this.bCustomerPermissionsEdited = false
    //this.goBackToSearchScreen()
    //return

    // Check if there is any difference between the origin profil and the profile from the screen
    // We need to save the same, if there is a difference
    // if (this.hasProfileDataChanged()){

    this.restApi.updateUserProfile(this.selectedItem.item).subscribe({
      next: (v: GenericResponse) => {
        if (v.success) {
          // Now save matrices
          this.restApi.updateUserMatrix(this.selectedItem.item.userId, this.finalMatrices).subscribe({
            next: (v: GenericResponse) => {
              //console.log(v)
              this.bCustomerProfileEdited = false
              this.bCustomerPermissionsEdited = false
              this.goBackToSearchScreen()
            },
            error: (e) => {

            }
          })
        } else {
          // Show error
        }

      },
      error: (e) => {
        console.log(e)
      }
    })
    // } else {
    //   console.log("No Profile changed")
    //   console.log("save2",this.finalMatrices)
    //   this.restApi.updateUserMatrix(this.selectedItem.item.userId, this.finalMatrices).subscribe({
    //     next: (v: GenericResponse) => {
    //       //console.log(v)
    //       this.bCustomerProfileEdited = false
    //       this.bCustomerPermissionsEdited = false
    //       this.goBackToSearchScreen()
    //     },
    //     error: (e) => {
    //       console.log(e)
    //     }
    //   })
    // } 
  }


  /*
    CREATE CUSTOMER
  */

  createCustomer() {
    this.bViewOnly = false
    this.selectedSearchButton = 4
    this.searchBtnColor = 'secondary'
    this.showRejectCustomerBtnColor = 'secondary'
    this.showAllRejectCustomerBtnColor = 'secondary'
    this.createCustomerBtnColor = 'primary'

    if (this.cifSearchForm.controls['searchCifControl'].value != null) {
      this.searchCif = this.cifSearchForm.controls['searchCifControl'].value
    }

    if (this.searchCif === '') {
      this.showAlertBox('Oops!', 'You need to enter a valid CIF')
      return
    }

    this.bLoading = true

    this.restApi.getCifDetails({ cif: this.searchCif }).subscribe({
      next: (v: CifDetails) => {
        if (v.success) {
          if (v.customerDetailsRes.responseCode === "000") {
            if (v.customerDetailsRes.ciftype === "COMPANY") {
              this.companyId = v.companyId
              this.companyName = v.customerDetailsRes.name1 + ', ' + v.customerDetailsRes.street + ', ' + v.customerDetailsRes.towncountry

              // Show the form
              this.showCreateCustomerScreen()
            }
            else {
              this.bLoading = false
              this.showAlertBox("Error", "The CIF " + this.searchCif + " is not a valid company. Please try with other CIF number")
            }
          }
        }
        else {
          if (v.customerDetailsRes.responseCode === "0025") {
            this.bLoading = false
            this.showAlertBox("Error", "No customers found for the provided CIF.")
          }
          else {
            this.bLoading = false
            this.showAlertBox("Error", "Oops! There was an error getting data from Middleware. Have patience and try after some time.")
          }
        }

      },
      error: (e) => {
        this.bLoading = false
        this.showAlertBox("Error", "Oops! Something went wrong. Have patience and try after some time.")
      },
    })
  }

  showCreateCustomerScreen() {


    this.bApprovedName = false
    this.bShowingApprovedName = false

    this.bApprovedEmail = false
    this.bShowingApprovedEmail = true

    this.bApprovedMobile = false
    this.bShowingApprovedMobile = false

    this.bApprovedGender = false
    this.bShowingApprovedGender = false

    this.bApprovedDOB = false
    this.bShowingApprovedDOB = false

    this.bApprovedIDType = false
    this.bShowingApprovedIDType = false

    this.bApprovedIDNumber = false
    this.bShowingApprovedIDNumber = false

    this.bApprovedNationality = false
    this.bShowingApprovedNationaility = false

    this.bApprovedIDExpiry = false
    this.bShowingApprovedIDExpiry = false

    this.resetCommomPermissions()
    this.resetMatrixControls()

    this.bSelectedCustomerCIFLevelAuthority = true
    this.bIsSelectedCustomerMaker = true
    this.bSelectedCustomerCreateSB = false
    this.bSelectedCustomerCreateCurrent = false
    this.bSelectedCustomerRequestCertificate = false
    this.bSelectedCustomerChangeMobile = false
    this.bSelectedCustomerChangeAddress = false
    this.bSelectedCustomerChangeEmail = false
    this.bSelectedCustomerFullMasterAuthority = false

    // CIF Level Authorities
    this.bSelectedCustomerViewOnly = false
    this.bSelectedCustomerDeactivateAccount = false
    this.bSelectedCustomerCanCreateFD = false
    this.bSelectedCustomerUnlimitedFDThreshold = false
    this.bSelectedCustomerCanMakeInternalTransfer = false
    this.bSelectedCustomerUnlimitedInternalTransferThreshold = false
    this.bSelectedCustomerCanMakeDhabiTransfer = false
    this.bSelectedCustomerUnlimitedDhabiTransferThreshold = false
    this.bSelectedCustomerCanMakeForeignTransfer = false
    this.bSelectedCustomerUnlimitedForeignTransferThreshold = false

    // Account Level Details
    this.SelectedCustomerAccount = ''
    this.authorityLevelTabLabel = 'CIF Level Authority'

    this.selectedTab = 0
    this.profileTabActive = true
    this.matrixTabActive = false
    this.bHasCustomerMatrixApprovalPending = false

    this.SelectedCustomerAccountPermissions = []

    this.bCustomerPermissionsEdited = false
    this.bCustomerProfileEdited = false

    this.bNewCustomer = true

    this.selectedItem.item = []
    this.selectedItem.item.cif = this.searchCif
    this.selectedItem.item.name = ''
    this.selectedItem.item.emailId = ''
    this.selectedItem.item.mobileNumber = ''
    this.selectedItem.item.genderId = 1
    this.selectedItem.item.dateOfBirth = ''
    this.selectedItem.item.isEID = false
    this.selectedItem.item.nationalityCountryCode = 'AE'
    this.selectedItem.item.expiresOn = ''
    this.selectedItem.item.approvalMatrices = []
    this.selectedCustomerRole = "Maker"
    this.selectedCustomerGender = 'Male'
    this.selectedCustomerDateOfBirth = this.minDateOfBirth
    this.selectedCustomerIDType = 'Passport'
    this.selectedCustomerNationality = 'AE'
    this.selectedCustomerIDExpiresOn = this.minIDExpiryDate

    this.selectedCustomerAuthorityLevel = 'CIF'
    this.bSelectedCustomerCIFLevelAuthority = true

    this.customerProfileForm = new FormGroup({
      customerName: new FormControl(this.selectedItem.item.name, [Validators.required, Validators.minLength(2), Validators.pattern(/^((?!\s{2,}).)*$/), Validators.maxLength(60)]),
      customerEmail: new FormControl(this.selectedItem.item.emailId, [Validators.required, Validators.pattern(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)]),
      customerMobile: new FormControl(this.selectedItem.item.mobileNumber, [Validators.required, Validators.minLength(0), Validators.maxLength(20), Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')]),
      customeGender: new FormControl(this.selectedCustomerGender, [Validators.required]),
      customeDateOfBirth: new FormControl(this.selectedCustomerDateOfBirth, [Validators.required]),
      customerIDType: new FormControl(this.selectedCustomerIDType, [Validators.required]),
      customerIDNumber: new FormControl(this.selectedItem.item.eidNumber, [Validators.required, Validators.minLength(5)]),
      customerNationality: new FormControl(this.selectedCustomerNationality, [Validators.required]),
      customerIDExpiryDate: new FormControl(this.selectedCustomerIDExpiresOn, [Validators.required, this.validateIDExpDate()])
    })

    this.customerProfileForm.valueChanges.subscribe(x => {
      this.bCustomerProfileEdited = true
    })

    this.customerNameValid = this.bUndefined
    this.customerEmailValid = this.bUndefined
    this.customerMobileValid = this.bUndefined
    this.customerGenderValid = this.bUndefined
    this.customerDOBValid = this.bUndefined
    this.customerIDTypeValid = this.bUndefined
    this.customerIDNumberValid = this.bUndefined
    this.customerNationalityValid = this.bUndefined
    this.customerIDExpiryValid = this.bUndefined

    this.bShowCifSearchScreen = false
    this.bShowCustomerDetailsScreen = true
  }

  saveNewCustomer() {
    // Put-in the additional details
    this.selectedItem.item.role = 1
    this.selectedItem.item.companyId = this.companyId
    console.log("saveNewCustomer", this.selectedItem.item)

    this.restApi.createCorporatecustomer(this.selectedItem.item).subscribe({
      next: (v: GenericResponse) => {
        if (v.success) {
          //console.log(v)
          this.bCustomerProfileEdited = false
          this.bCustomerPermissionsEdited = false
          this.goBackToSearchScreen()
        } else {
          this.bLoading = false
          this.showAlertBox("Sorry", v.message)
        }
      },
      error: (e) => {
        this.bLoading = false
        this.showAlertBox("Error", "Oops! Something went wrong. Have patience and try after some time.")
      }
    })
  }

  /*
    GET REJECTED CUSTOMERS
  */
  getRejectedCustomerCif() {
    this.bViewOnly = true

    this.selectedSearchButton = 2
    this.searchBtnColor = 'secondary'
    this.showRejectCustomerBtnColor = 'primary'
    this.showAllRejectCustomerBtnColor = 'secondary'
    this.createCustomerBtnColor = 'secondary'

    if (this.cifSearchForm.controls['searchCifControl'].value != null) {
      this.searchCif = this.cifSearchForm.controls['searchCifControl'].value
    }

    if (this.searchCif === '') {
      this.showAlertBox('Oops!!', 'You need to enter a valid CIF')
      return
    }

    this.bLoading = true

    this.restApi.getCifDetails({ cif: this.searchCif }).subscribe({
      next: (v: CifDetails) => {
        if (v.success) {
          if (v.customerDetailsRes.responseCode === "000") {
            if (v.customerDetailsRes.ciftype === "COMPANY") {
              this.companyId = v.companyId
              this.companyName = v.customerDetailsRes.name1 + ', ' + v.customerDetailsRes.street + ', ' + v.customerDetailsRes.towncountry
              // Show the form
              this.getRejectedCustomers()
            }
            else {
              this.bLoading = false
              this.showAlertBox("Error", "The CIF " + this.searchCif + " is not a valid company. Please try with other CIF number!")
            }
          }
        }
        else {
          if (v.customerDetailsRes.responseCode === "0025") {
            this.bLoading = false
            this.showAlertBox("Error", "No customers found for the provided CIF.")
          }
          else {
            this.bLoading = false
            this.showAlertBox("Error", "Oops! There was an error getting data from Middleware. Have patience and try after some time.")
          }
        }

      },
      error: (e) => {
        this.bLoading = false
        this.showAlertBox("Error", "Oops! Something went wrong. Have patience and try after some time.")
      },
    })
  }

  getRejectedCustomers() {

    this.restApi.getRejectedCorporateCustomers({ cif: this.searchCif }).subscribe({
      next: (v: CorporateUserDetails) => {
        console.log(v)

        this.bLoading = false
        this.usersData = v.corporateUsers.map((item, UserId) => {
          const _selected = this.selectedItems.includes(UserId);
          return {
            ...item,
            _selected
          };
        })
      },
      error: (e) => {
        this.bLoading = false
      }
    })

  }

  getAllRejectedCustomers() {

    this.bViewOnly = true
    this.selectedSearchButton = 3
    this.cifSearchForm.controls['searchCifControl'].setValue('')
    this.companyName = ''
    this.searchBtnColor = 'secondary'
    this.showRejectCustomerBtnColor = 'secondary'
    this.showAllRejectCustomerBtnColor = 'primary'
    this.createCustomerBtnColor = 'secondary'

    this.restApi.getRejectedCorporateCustomers({ cif: 'ALL' }).subscribe({
      next: (v: CorporateUserDetails) => {
        this.bLoading = false
        this.usersData = v.corporateUsers.map((item, UserId) => {
          const _selected = this.selectedItems.includes(UserId);
          return {
            ...item,
            _selected
          };
        })
      },
      error: (e) => {
        this.bLoading = false
      }
    })
  }

  deactivateCustomerPrompt(value: any) {
    //console.log(this.selectedItem.item.isActive)
    if (value == 1)

      this.showAlertBox('Confirm', 'Are you sure you want to DEACTIVATE this customer?', 'No', true, 'Yes', { action: 8, matrixId: 0 })
    else
      this.showAlertBox('Confirm', 'Are you sure you want to ACTIVATE this customer?', 'No', true, 'Yes', { action: 8, matrixId: 0 })
  }

  activateOrDeactivateCustomer() {
    let activateAction = false

    if (this.selectedItem.item.isActive)
      activateAction = false
    else
      activateAction = true

    this.restApi.activateOrDeactivateCustomerRequest(this.selectedItem.item.userId, activateAction).subscribe({
      next: (v: GenericResponse) => {
        if (v.success) {
          this.goBackToSearchScreen();
        } else {
          this.showAlertBox('Sorry', 'There were some technical issues. Please contact the technical support team.')
        }
      },
      error: (e) => {
        this.showAlertBox('Sorry', 'There were some technical issues. Please contact the technical support team.')
      }
    })
  }
}

